<template>
    <div class="punch-competition-page">
        <!-- ================================================================= -->
        <!-- 1. AGE GATE CONFIRMATION                                          -->
        <!-- ================================================================= -->
        <ConfirmAge1 v-if="showAgeConfirm" @confirmed="onAgeConfirmed" />

        <!-- ================================================================= -->
        <!-- 2. ROLE SELECTION POPUP MODAL (After Age Gate)                    -->
        <!-- ================================================================= -->
        <transition name="modal-scale">
            <div v-if="!showAgeConfirm && (!selectedRole || showRoleModal)" class="role-modal-backdrop" @click.self="handleBackdropClick">
                <div class="role-modal-card">
                    <!-- Close button if re-opened via "Сменить роль" -->
                    <button
                        v-if="selectedRole && showRoleModal"
                        class="role-card-close-icon"
                        @click="showRoleModal = false"
                        aria-label="Закрыть"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>

                    <div class="role-card-content">
                        <!-- J Logo -->
                        <div class="role-modal-logo-wrapper">
                            <img src="@/assets/img/j-invicible.png" alt="Jameson J" class="role-modal-j-logo" />
                        </div>

                        <!-- Serif Uppercase Title -->
                        <h1 class="role-modal-title">ДОБРО ПОЖАЛОВАТЬ!</h1>

                        <!-- Subtitle -->
                        <p class="role-modal-subtitle">Выберите вашу роль</p>

                        <!-- Buttons -->
                        <div class="role-modal-buttons">
                            <button class="role-choice-btn" @click="chooseRole('bartender')">
                                <span class="btn-text">Я БАРМЕН</span>
                            </button>
                            <button class="role-choice-btn" @click="chooseRole('enthusiast')">
                                <span class="btn-text">Я ЛЮБИТЕЛЬ ПУНШЕЙ</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- ================================================================= -->
        <!-- 3. MAIN LANDING CONTENT (Loaded after Role is selected)           -->
        <!-- ================================================================= -->
        <div v-if="!showAgeConfirm && selectedRole" class="content-wrapper">
            <!-- ROLE SWITCHER TOP BAR -->
            <div class="role-topbar">
                <div class="role-topbar-inner">
                    <div class="current-role-badge">
                        <span class="badge-dot"></span>
                        <span class="badge-label">Вы просматриваете:</span>
                        <strong class="badge-value">{{ currentRoleTitle }}</strong>
                    </div>
                    <button class="switch-role-btn" @click="showRoleModal = true">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="1 4 1 10 7 10"></polyline>
                            <polyline points="23 20 23 14 17 14"></polyline>
                            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"></path>
                        </svg>
                        <span>Сменить роль</span>
                    </button>
                </div>
            </div>

            <!-- ============================================================= -->
            <!-- BLOCKS 1 & 2 (COMMON): HERO & COUNTDOWN TIMER SECTION         -->
            <!-- ============================================================= -->
            <div class="block-hero-timer-section">
                <div class="landing-content-container">
                    <!-- BLOCK 1: HERO -->
                    <section class="block-hero">
                        <!-- Top White Jameson Logo -->
                        <div class="hero-logo-wrapper">
                            <img src="@/assets/img/jameson-logo-age-confirm.png" alt="JAMESON" class="jameson-white-logo" />
                        </div>

                        <!-- Main Heading: Very large, bold, condensed uppercase -->
                        <h1 class="hero-main-title">JAMESON PUNCH COMPETITION</h1>

                        <!-- Subtitle: Regular sans-serif font -->
                        <p class="hero-subtitle">
                            <template v-if="selectedRole === 'bartender'">Конкурс для профессиональных барменов</template>
                            <template v-else>Мы в поисках идеального рецепта пунша</template>
                        </p>
                    </section>

                    <!-- BLOCK 2: TIMER AND INFO -->
                    <section class="block-timer-info">
                        <!-- Gold / Mustard Divider Line -->
                        <div class="gold-divider-line"></div>

                        <!-- Timer Heading -->
                        <p class="timer-heading">До дедлайна заявок</p>

                        <!-- 4 Timer Cards (Flex Row) -->
                        <div class="countdown-timer-grid">
                            <div class="timer-card">
                                <span class="timer-digit">{{ formattedTimer.days }}</span>
                                <span class="timer-unit">ДНЕЙ</span>
                            </div>
                            <div class="timer-card">
                                <span class="timer-digit">{{ formattedTimer.hours }}</span>
                                <span class="timer-unit">ЧАСОВ</span>
                            </div>
                            <div class="timer-card">
                                <span class="timer-digit">{{ formattedTimer.minutes }}</span>
                                <span class="timer-unit">МИНУТ</span>
                            </div>
                            <div class="timer-card">
                                <span class="timer-digit">{{ formattedTimer.seconds }}</span>
                                <span class="timer-unit">СЕКУНД</span>
                            </div>
                        </div>

                        <!-- Text below timer -->
                        <p class="timer-deadline-date">10 сентября 2026, 23:59</p>
                    </section>
                </div>
            </div>

            <!-- ============================================================= -->
            <!-- BLOCK: ЧТО ТАКОЕ JAMESON PUNCH COMPETITION (ОТДЕЛЬНЫЙ БЛОК)    -->
            <!-- ============================================================= -->
            <section class="block-about-competition-section about-jameson-punch">
                <div class="landing-content-container">
                    <h2 class="about-competition-heading">ЧТО ТАКОЕ JAMESON PUNCH COMPETITION?</h2>
                    <!-- ~800px text paragraph (Role-specific) -->
                    <p class="about-competition-text" v-if="selectedRole === 'bartender'">
                        Jameson Punch Competition 2026 — это уникальная возможность для профессиональных барменов Казахстана показать своё мастерство, креативность и глубокое понимание характера ирландского виски. Создайте оригинальный пунш, вдохновленный философией Jameson, и участвуйте в конкурсе!
                    </p>
                    <p class="about-competition-text" v-else>
                        Jameson Punch Competition 2026 — это поиск идеального домашнего рецепта пунша от любителей Казахстана. Поделитесь своим уникальным рецептом, который вы готовите для друзей и близких, и станьте частью сообщества!                    </p>
                </div>
            </section>

            <!-- ============================================================= -->
            <!-- >>>>>>>>>>>>>>>>>> FLOW 1: Я БАРМЕН <<<<<<<<<<<<<<<<<<<<<<<< -->
            <!-- ============================================================= -->
            <template v-if="selectedRole === 'bartender'">
                <!-- BLOCK 3 (BARTENDER): ДЛЯ КОГО ЭТОТ КОНКУРС (#0d7c49) -->
                <section class="block-bartender-target-section">
                    <div class="target-container-inner">
                        <div class="target-two-columns-layout">
                            <!-- Left Column (Text: 60%) -->
                            <div class="target-column-left">
                                <h2 class="target-main-heading">ДЛЯ КОГО ЭТОТ КОНКУРС</h2>
                                <p class="target-lead-paragraph">
                                    Приглашаем профессиональных барменов, работающих в барах, ресторанах и иных заведениях общественного питания.
                                </p>
                                <ul class="bartender-requirements-list">
                                    <li>Возраст от 21 года;</li>
                                    <li>Работающий бармен, барбэк, бар-менеджер или шеф-бармен;</li>
                                    <li>Резидент РК с постоянным местом проживания;</li>
                                    <li>По одной заявке на участника.</li>
                                </ul>
                            </div>

                            <!-- Right Column (Image: 40%) -->
                            <div class="target-column-right">
                                <img src="@/assets/img/form-punch-image.png" alt="Jameson Bottles" class="target-bottles-image" />
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 4 (BARTENDER): ЭТАПЫ КОНКУРСА (#114b35) -->
                <section class="block-stages-section">
                    <div class="stages-container-inner">
                        <div class="section-title-left-wrap">
                            <h2 class="section-title-left">ЭТАПЫ КОНКУРСА</h2>
                        </div>

                        <!-- Vertical Timeline (3 Stages) -->
                        <div class="stages-timeline-container">
                            <!-- Stage 1 -->
                            <div class="timeline-stage-row">
                                <div class="timeline-marker-col">
                                    <div class="timeline-marker-circle">1</div>
                                    <div class="timeline-marker-line"></div>
                                </div>
                                <div class="timeline-content-card">
                                    <div class="stage-card-left">
                                        <h3 class="stage-name-title">ОНЛАЙН ЗАЯВКА</h3>
                                        <p class="stage-dates-text">27 августа — 10 сентября</p>
                                    </div>
                                    <div class="stage-card-divider"></div>
                                    <div class="stage-card-right">
                                        <p class="stage-desc-text">
                                            Подача заявки с рецептом, пропорциями, описанием техники приготовления.
                                        </p>
                                        <img src="@/assets/img/step-1.png" alt="Онлайн заявка" class="stage-step-image" />
                                    </div>
                                </div>
                            </div>

                            <!-- Stage 2 -->
                            <div class="timeline-stage-row">
                                <div class="timeline-marker-col">
                                    <div class="timeline-marker-circle">2</div>
                                    <div class="timeline-marker-line"></div>
                                </div>
                                <div class="timeline-content-card">
                                    <div class="stage-card-left">
                                        <h3 class="stage-name-title">ОТБОР ТОП 10</h3>
                                        <p class="stage-dates-text">10 — 17 сентября</p>
                                    </div>
                                    <div class="stage-card-divider"></div>
                                    <div class="stage-card-right">
                                        <p class="stage-desc-text">
                                            Жюри оценивает заявки. Максимум 90 баллов.
                                        </p>
                                        <img src="@/assets/img/step-2.png" alt="Отбор ТОП 10" class="stage-step-image" />
                                    </div>
                                </div>
                            </div>

                            <!-- Stage 3 -->
                            <div class="timeline-stage-row">
                                <div class="timeline-marker-col">
                                    <div class="timeline-marker-circle">3</div>
                                </div>
                                <div class="timeline-content-card">
                                    <div class="stage-card-left">
                                        <h3 class="stage-name-title">ФИНАЛ В АЛМАТЫ</h3>
                                        <p class="stage-dates-text">24 сентября</p>
                                    </div>
                                    <div class="stage-card-divider"></div>
                                    <div class="stage-card-right">
                                        <p class="stage-desc-text">
                                            ТОП 5 финалистов готовят пунш. Оценка жюри (50%) + голосование гостей (50%).
                                        </p>
                                        <img src="@/assets/img/step-3.png" alt="Финал в Алматы" class="stage-step-image" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 5 (BARTENDER): СУДЕЙСКАЯ КОЛЛЕГИЯ -->
                <section class="block-judges-section">
                    <div class="landing-content-container">
                        <div class="section-title-left-wrap">
                            <h2 class="section-title-left">СУДЕЙСКАЯ КОЛЛЕГИЯ</h2>
                        </div>

                        <!-- Judges Grid (3 top, 1 bottom center) -->
                        <div class="judges-cards-container">
                            <div class="judges-top-row">
                                <!-- Judge 1 -->
                                <div class="judge-card-item">
                                    <div class="judge-photo-wrapper">
                                        <img src="@/assets/img/judge-1.png" alt="Леонид Никитенко" class="judge-photo-img" />
                                    </div>
                                    <h3 class="judge-name-title">ЛЕОНИД НИКИТЕНКО</h3>
                                    <p class="judge-role-subtitle">Бренд-амбассадор Jameson</p>
                                </div>

                                <!-- Judge 2 -->
                                <div class="judge-card-item">
                                    <div class="judge-photo-wrapper">
                                        <img src="@/assets/img/judge-2.png" alt="Тимофей Бишкин" class="judge-photo-img" />
                                    </div>
                                    <h3 class="judge-name-title">ТИМОФЕЙ БИШКИН</h3>
                                    <p class="judge-role-subtitle">Бар-менеджер баров Jameson Camp и El Agave Sindicato</p>
                                </div>

                                <!-- Judge 3 -->
                                <div class="judge-card-item">
                                    <div class="judge-photo-wrapper">
                                        <img src="@/assets/img/judge-3.png" alt="Вероника Ким" class="judge-photo-img" />
                                    </div>
                                    <h3 class="judge-name-title">ВЕРОНИКА КИМ</h3>
                                    <p class="judge-role-subtitle">Блогер & Content Creator</p>
                                </div>
                            </div>

                            <!-- Bottom Row: 1 Judge Centered -->
                            <div class="judges-bottom-row">
                                <div class="judge-card-item judge-card-consumer-winner">
                                    <div class="judge-photo-wrapper judge-photo-placeholder-wrapper">
                                        <img src="@/assets/img/judge-4.png" alt="Победитель конкурса потребителей" class="judge-photo-img judge-placeholder-bg" />
                                        <div class="judge-placeholder-overlay">
                                            <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#114b35" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                                                <circle cx="12" cy="8" r="7"></circle>
                                                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                                            </svg>
                                        </div>
                                    </div>
                                    <h3 class="judge-name-title">ПОБЕДИТЕЛЬ КОНКУРСА ПОТРЕБИТЕЛЕЙ</h3>
                                    <p class="judge-role-subtitle">Судья финала</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 6 (BARTENDER): ТРЕБОВАНИЯ К ЗАЯВКЕ (#114b35) -->
                <section class="block-requirements-section">
                    <div class="requirements-container-inner">
                        <div class="section-title-left-wrap">
                            <h2 class="section-title-left">ТРЕБОВАНИЯ К ЗАЯВКЕ</h2>
                        </div>

                        <div class="requirements-columns-grid">
                            <!-- Left Column: РЕЦЕПТ ПУНША -->
                            <div class="requirement-card-column">
                                <div class="req-badge-header">
                                    <span>РЕЦЕПТ ПУНША</span>
                                </div>
                                <ul class="req-checklist">
                                    <li>
                                        <div class="req-check-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                        <span>На основе Jameson (минимум 100 мл)</span>
                                    </li>
                                    <li>
                                        <div class="req-check-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                        <span>Общий объем минимум 600 мл без льда</span>
                                    </li>
                                    <li>
                                        <div class="req-check-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                        <span>Полный перечень ингредиентов с точными количествами</span>
                                    </li>
                                </ul>
                                <div class="req-column-bottom-divider"></div>
                            </div>

                            <!-- Right Column: INSTAGRAM ПОСТ -->
                            <div class="requirement-card-column">
                                <div class="req-badge-header">
                                    <span>INSTAGRAM ПОСТ</span>
                                </div>
                                <ul class="req-checklist">
                                    <li>
                                        <div class="req-check-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                        <span>Фотография готового пунша</span>
                                    </li>
                                    <li>
                                        <div class="req-check-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                        <span>Видимая бутылка Jameson</span>
                                    </li>
                                    <li>
                                        <div class="req-check-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        </div>
                                        <span>Хештег #jamesonpunchcompetition</span>
                                    </li>
                                </ul>
                                <div class="req-column-bottom-divider"></div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 7 (BARTENDER): ПРИЗЫ БАРМЕНОВ (#0d7c49) -->
                <section class="block-bartender-prizes-section">
                    <div class="bartender-prizes-inner">
                        <div class="section-title-left-wrap">
                            <h2 class="section-title-left">ПРИЗЫ</h2>
                        </div>

                        <div class="bartender-prizes-grid">
                            <!-- Card 1: ГЛАВНЫЙ ПРИЗ -->
                            <div class="bartender-prize-card">
                                <div class="bartender-prize-photo-placeholder">
                                    <div class="bartender-prize-icon-inner">
                                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M17.8 19.2L16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.3c.4-.2.6-.6.5-1.1z"></path>
                                        </svg>
                                    </div>
                                </div>
                                <h3 class="bartender-prize-heading">ГЛАВНЫЙ ПРИЗ</h3>
                                <p class="bartender-prize-desc">Поездка в Афины на Athens Bar Show 2026.</p>
                            </div>

                            <!-- Card 2: ВЫБОР ЖЮРИ -->
                            <div class="bartender-prize-card">
                                <div class="bartender-prize-photo-placeholder">
                                    <div class="bartender-prize-icon-inner">
                                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                            <circle cx="12" cy="8" r="7"></circle>
                                            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                                        </svg>
                                    </div>
                                </div>
                                <h3 class="bartender-prize-heading">ВЫБОР ЖЮРИ</h3>
                                <p class="bartender-prize-desc">Специальная награда от Jameson.</p>
                            </div>

                            <!-- Card 3: ПРИЗНАНИЕ -->
                            <div class="bartender-prize-card">
                                <div class="bartender-prize-photo-placeholder">
                                    <div class="bartender-prize-icon-inner">
                                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                                            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                                        </svg>
                                    </div>
                                </div>
                                <h3 class="bartender-prize-heading">ПРИЗНАНИЕ</h3>
                                <p class="bartender-prize-desc">Публикация в официальных аккаунтах Jameson.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 8 (BARTENDER): FAQ (#0c3826) -->
                <section class="block-faq-section">
                    <div class="faq-container-inner">
                        <div class="section-title-left-wrap">
                            <h2 class="section-title-left">ЧАСТО ЗАДАВАЕМЫЕ ВОПРОСЫ</h2>
                        </div>

                        <div class="faq-accordion-list">
                            <div
                                class="faq-accordion-item"
                                v-for="(item, index) in bartenderFaqs"
                                :key="index"
                            >
                                <div class="faq-item-header" @click="toggleFaq(index)">
                                    <span class="faq-question-title">{{ item.q }}</span>
                                    <div class="faq-toggle-icon" :class="{ 'is-open': item.isOpen }">
                                        <svg v-if="!item.isOpen" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                            <line x1="12" y1="5" x2="12" y2="19"></line>
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                        </svg>
                                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                                            <line x1="5" y1="12" x2="19" y2="12"></line>
                                        </svg>
                                    </div>
                                </div>

                                <transition name="faq-collapse">
                                    <div class="faq-item-body" v-if="item.isOpen">
                                        <p class="faq-answer-text">{{ item.a }}</p>
                                    </div>
                                </transition>

                                <div class="faq-item-divider"></div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 9 (BARTENDER): CTA (#0d7c49) -->
                <section class="block-cta-section">
                    <div class="cta-container-inner">
                        <h2 class="cta-main-heading">ГОТОВЫ К УЧАСТИЮ?</h2>
                        <p class="cta-main-text">
                            Подготовьте ваш рецепт и опубликуйте пост в Instagram. Затем заполните форму заявки.
                        </p>
                        <button class="cta-ghost-btn" @click="handleOpenBartenderForm">
                            <span class="btn-text">ПОДАТЬ ЗАЯВКУ</span>
                        </button>
                    </div>
                </section>

                <!-- BLOCKS 11 & 12 (BARTENDER): СКРЫТЫ ДО НАЖАТИЯ «ПОДАТЬ ЗАЯВКУ» -->
                <transition name="form-fade">
                    <div v-if="showBartenderForm" class="bartender-revealed-form-flow">
                        <!-- BLOCK 11 (BARTENDER): ПРЕДУПРЕЖДЕНИЕ ПЕРЕД ФОРМОЙ (#0c3826) -->
                        <section ref="bartenderNoticeSection" id="bartender-notice" class="block-form-notice-section">
                            <div class="notice-container-inner">
                                <h2 class="notice-main-heading">📸 ПЕРЕД ПОДАЧЕЙ ЗАЯВКИ</h2>
                                <p class="notice-subheading">Важно!</p>
                                <p class="notice-description-text">
                                    Опубликуйте фотографию вашего готового пунша в Instagram с хештегом #jamesonpunchcompetition и упоминая @JamesonKZ. Видимая бутылка Jameson обязательна! Затем заполните эту форму с полной информацией о вашем рецепте.
                                </p>
                            </div>
                        </section>

                        <!-- BLOCK 12 (BARTENDER): САМА ФОРМА ЗАЯВКИ (#0d7c49) -->
                        <section ref="bartenderFormSection" id="bartender-form" class="block-bartender-full-form-section">
                            <div class="full-form-container-inner">
                                <div v-if="isLoadingForm" class="form-loading-wrap">
                                    <v-progress-circular indeterminate size="48" color="#FEF8E1" />
                                </div>
                                <div v-else-if="formError" class="form-error-wrap">
                                    <p class="form-error-text">Ошибка загрузки формы. Попробуйте позже.</p>
                                    <button type="button" class="ghost-action-btn clear-btn mt-3" @click="getFormFields(currentCampaignId)">
                                        Повторить попытку
                                    </button>
                                </div>
                                <form v-else @submit.prevent="confirmRegistration" class="bartender-application-form">
                                    <!-- СЕКЦИЯ 1: ВАШИ КОНТАКТЫ (2 колонки) -->
                                    <div class="form-section-group" v-if="bartenderSections.contacts.length > 0">
                                        <h3 class="form-section-title">ВАШИ КОНТАКТЫ</h3>
                                        <div class="form-grid-two-cols">
                                            <div
                                                v-for="question in bartenderSections.contacts"
                                                :key="question.id"
                                                class="form-input-control"
                                            >
                                                <label class="input-top-label">{{ question.name }}*</label>

                                                <!-- Телефон (4) -->
                                                <input
                                                    v-if="question.type === 4"
                                                    type="tel"
                                                    class="transparent-form-input"
                                                    placeholder="+7 (___) ___-__-__"
                                                    v-mask="'+7(###) ###-##-##'"
                                                    v-model="formData[question.id]"
                                                    @focus="onPhoneFocus(question)"
                                                    @input="onPhoneInput(question)"
                                                    @blur="markTouched(question)"
                                                />

                                                <!-- Выпадающий список (Должность, Релиз, Город или любой список) -->
                                                <v-select
                                                    v-else-if="isSelect(question)"
                                                    v-model="formData[question.id]"
                                                    :items="question.answers"
                                                    item-text="name"
                                                    item-value="id"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :placeholder="getPlaceholder(question)"
                                                    class="custom-v-select"
                                                    :menu-props="{ bottom: true, offsetY: true, contentClass: 'punch-select-menu' }"
                                                    @change="validateField(question)"
                                                    @blur="markTouched(question)"
                                                >
                                                    <template v-slot:selection="{ item }">
                                                        <div class="select-selected-text">{{ item ? item.name : getPlaceholder(question) }}</div>
                                                    </template>
                                                </v-select>

                                                <!-- Текст / Email / Default -->
                                                <input
                                                    v-else
                                                    :type="question.type === 7 ? 'email' : 'text'"
                                                    class="transparent-form-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />

                                                <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 2: О ВАШЕМ ПУНШЕ (2 поля 100%, затем 2 поля 50/50) -->
                                    <div class="form-section-group" v-if="bartenderSections.punchTop.length > 0 || bartenderSections.punchBottom.length > 0">
                                        <h3 class="form-section-title">О ВАШЕМ ПУНШЕ</h3>

                                        <!-- Первые два поля на 100% ширины (Название пунша, Концепция и вдохновение) -->
                                        <div
                                            v-for="question in bartenderSections.punchTop"
                                            :key="question.id"
                                            class="form-input-control full-width-control"
                                        >
                                            <label class="input-top-label">{{ question.name }}*</label>
                                            <div class="textarea-wrapper" v-if="isTextarea(question)">
                                                <textarea
                                                    class="transparent-form-input transparent-textarea"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                    maxlength="800"
                                                    rows="4"
                                                ></textarea>
                                                <div class="char-counter" :class="{ 'char-counter-limit': (formData[question.id] || '').length >= 800 }">
                                                    {{ (formData[question.id] || '').length }} / 800
                                                </div>
                                            </div>
                                            <input
                                                v-else
                                                type="text"
                                                class="transparent-form-input"
                                                :placeholder="getPlaceholder(question)"
                                                v-model="formData[question.id]"
                                                @input="validateField(question)"
                                                @blur="markTouched(question)"
                                            />
                                            <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                        </div>

                                        <!-- Следующие два поля в 2 колонки (50/50) (Релиз Jameson, Общий объем) -->
                                        <div class="form-grid-two-cols" v-if="bartenderSections.punchBottom.length > 0">
                                            <div
                                                v-for="question in bartenderSections.punchBottom"
                                                :key="question.id"
                                                class="form-input-control"
                                            >
                                                <label class="input-top-label">{{ question.name }}*</label>
                                                <v-select
                                                    v-if="isSelect(question)"
                                                    v-model="formData[question.id]"
                                                    :items="question.answers"
                                                    item-text="name"
                                                    item-value="id"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :placeholder="getPlaceholder(question)"
                                                    class="custom-v-select"
                                                    :menu-props="{ bottom: true, offsetY: true, contentClass: 'punch-select-menu' }"
                                                    @change="validateField(question)"
                                                    @blur="markTouched(question)"
                                                >
                                                    <template v-slot:selection="{ item }">
                                                        <div class="select-selected-text">{{ item ? item.name : getPlaceholder(question) }}</div>
                                                    </template>
                                                </v-select>
                                                <input
                                                    v-else
                                                    type="text"
                                                    class="transparent-form-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />
                                                <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 3: ПОЛНЫЙ РЕЦЕПТ (2 поля 50/50 textarea, 1 поле 100% textarea) -->
                                    <div class="form-section-group" v-if="bartenderSections.recipeTop.length > 0 || bartenderSections.recipeBottom.length > 0">
                                        <h3 class="form-section-title">ПОЛНЫЙ РЕЦЕПТ</h3>

                                        <!-- Первые два поля в 2 колонки (50/50): Ингредиенты и Техника -->
                                        <div class="form-grid-two-cols" v-if="bartenderSections.recipeTop.length > 0">
                                            <div
                                                v-for="question in bartenderSections.recipeTop"
                                                :key="question.id"
                                                class="form-input-control"
                                            >
                                                <label class="input-top-label">{{ question.name }}*</label>
                                                <div class="textarea-wrapper">
                                                    <textarea
                                                        class="transparent-form-input transparent-textarea"
                                                        :placeholder="getPlaceholder(question)"
                                                        v-model="formData[question.id]"
                                                        @input="validateField(question)"
                                                        @blur="markTouched(question)"
                                                        maxlength="800"
                                                        rows="5"
                                                    ></textarea>
                                                    <div class="char-counter" :class="{ 'char-counter-limit': (formData[question.id] || '').length >= 800 }">
                                                        {{ (formData[question.id] || '').length }} / 800
                                                    </div>
                                                </div>
                                                <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>

                                        <!-- Третье поле на 100% ширины: Домашние заготовки -->
                                        <div
                                            v-for="question in bartenderSections.recipeBottom"
                                            :key="question.id"
                                            class="form-input-control full-width-control"
                                        >
                                            <label class="input-top-label">{{ question.name }}*</label>
                                            <div class="textarea-wrapper">
                                                <textarea
                                                    class="transparent-form-input transparent-textarea"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                    maxlength="800"
                                                    rows="4"
                                                ></textarea>
                                                <div class="char-counter" :class="{ 'char-counter-limit': (formData[question.id] || '').length >= 800 }">
                                                    {{ (formData[question.id] || '').length }} / 800
                                                </div>
                                            </div>
                                            <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 4: INSTAGRAM (2 колонки 50/50) -->
                                    <div class="form-section-group" v-if="bartenderSections.instagram.length > 0">
                                        <h3 class="form-section-title">INSTAGRAM</h3>
                                        <div class="form-grid-two-cols">
                                            <div
                                                v-for="question in bartenderSections.instagram"
                                                :key="question.id"
                                                class="form-input-control"
                                            >
                                                <label class="input-top-label">{{ question.name }}*</label>
                                                <input
                                                    :type="isUrlField(question) ? 'url' : 'text'"
                                                    class="transparent-form-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />
                                                <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 5: СОГЛАСИЕ (1 колонка друг под другом) -->
                                    <div class="form-section-group consent-section-group" v-if="bartenderSections.consents.length > 0">
                                        <h3 class="form-section-title">СОГЛАСИЕ</h3>
                                        <div class="consent-checkboxes-list">
                                            <div v-for="(question, idx) in bartenderSections.consents" :key="question.id" class="checkbox-wrapper">
                                                <div class="checkbox-row-item">
                                                    <input
                                                        type="checkbox"
                                                        :id="'chk-bartender-' + question.id"
                                                        class="custom-check-input"
                                                        v-model="formData[question.id]"
                                                        @change="markTouched(question)"
                                                    />
                                                    <label :for="'chk-bartender-' + question.id" class="custom-check-label">
                                                        <template v-if="getCheckboxInfo(question, idx).isSpecial">
                                                            {{ getCheckboxInfo(question, idx).prefix }}
                                                            <a class="policy-link-inline" @click.stop.prevent="openPopup(getCheckboxInfo(question, idx).popup)">
                                                                {{ getCheckboxInfo(question, idx).linkText }}
                                                            </a>
                                                        </template>
                                                        <template v-else>
                                                            {{ getCheckboxInfo(question, idx).text }}
                                                        </template>
                                                    </label>
                                                </div>
                                                <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- ДОПОЛНИТЕЛЬНЫЕ ПОЛЯ (ЕСЛИ ЕСТЬ НЕРАСПРЕДЕЛЕННЫЕ) -->
                                    <div class="form-section-group" v-if="bartenderSections.extra.length > 0">
                                        <div class="form-grid-two-cols">
                                            <div
                                                v-for="question in bartenderSections.extra"
                                                :key="question.id"
                                                class="form-input-control"
                                                :class="{ 'full-width-control': isFullWidth(question) }"
                                            >
                                                <label class="input-top-label">{{ question.name }}*</label>
                                                <v-select
                                                    v-if="isSelect(question)"
                                                    v-model="formData[question.id]"
                                                    :items="question.answers"
                                                    item-text="name"
                                                    item-value="id"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :placeholder="getPlaceholder(question)"
                                                    class="custom-v-select"
                                                    :menu-props="{ bottom: true, offsetY: true, contentClass: 'punch-select-menu' }"
                                                    @change="validateField(question)"
                                                    @blur="markTouched(question)"
                                                >
                                                    <template v-slot:selection="{ item }">
                                                        <div class="select-selected-text">{{ item ? item.name : getPlaceholder(question) }}</div>
                                                    </template>
                                                </v-select>
                                                <div class="textarea-wrapper" v-else-if="isTextarea(question)">
                                                    <textarea
                                                        class="transparent-form-input transparent-textarea"
                                                        :placeholder="getPlaceholder(question)"
                                                        v-model="formData[question.id]"
                                                        @input="validateField(question)"
                                                        @blur="markTouched(question)"
                                                        maxlength="800"
                                                        :rows="3"
                                                    ></textarea>
                                                    <div class="char-counter" :class="{ 'char-counter-limit': (formData[question.id] || '').length >= 800 }">
                                                        {{ (formData[question.id] || '').length }} / 800
                                                    </div>
                                                </div>
                                                <input
                                                    v-else
                                                    type="text"
                                                    class="transparent-form-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />
                                                <span v-if="errors[question.id]" class="form-field-error">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ КНОПОК: ДВЕ КНОПКИ В ОДИН РЯД -->
                                    <div class="bartender-form-buttons-row">
                                        <button type="submit" class="bartender-btn-submit" :disabled="submitting || isSendSms">
                                            <span v-if="!submitting && !isSendSms">ПОДАТЬ ЗАЯВКУ</span>
                                            <span v-else>ОТПРАВКА...</span>
                                        </button>
                                        <button type="button" class="bartender-btn-clear" @click="handleClearCurrentForm">
                                            ОЧИСТИТЬ ФОРМУ
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </section>
                    </div>
                </transition>
            </template>

            <!-- ============================================================= -->
            <!-- >>>>>>>>>>>>>>> FLOW 2: Я ЛЮБИТЕЛЬ ПУНШЕЙ <<<<<<<<<<<<<<<<<<< -->
            <!-- ============================================================= -->
            <template v-else>
                <!-- BLOCK 3 (ENTHUSIAST): О ЧЁМ ЭТО? (#1a684b) -->
                <section class="block-enthusiast-about-section">
                    <div class="about-container-inner">
                        <h2 class="about-main-title">О ЧЁМ ЭТО?</h2>
                        <p class="about-main-text">
                            Мы собираем рецепты домашних пуншей на основе Jameson от любителей. Поделитесь своим оригинальным рецептом, который вы готовите для друзей и примите участие в конкурсе. Процесс простой:
                        </p>

                        <div class="enthusiast-steps-list">
                            <div class="enthusiast-step-item">
                                <div class="enthusiast-step-number">1</div>
                                <div class="enthusiast-step-content">
                                    <p class="enthusiast-step-text">Придумай свой пунш, создай оригинальный и легко повторяемый рецепт.</p>
                                </div>
                            </div>

                            <div class="enthusiast-step-item">
                                <div class="enthusiast-step-number">2</div>
                                <div class="enthusiast-step-content">
                                    <p class="enthusiast-step-text">Оставь заявку на сайте</p>
                                </div>
                            </div>

                            <div class="enthusiast-step-item">
                                <div class="enthusiast-step-number">3</div>
                                <div class="enthusiast-step-content">
                                    <p class="enthusiast-step-text">Дождись результатов отбора — жюри конкурса выберет 3 лучших рецепта, приготовит их и проведёт дегустацию, учитывая вкус, оригинальность и возможность легко повторить пунш дома.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 4 (ENTHUSIAST): ПРИЗЫ -->
                <section class="block-enthusiast-prizes-section">
                    <div class="landing-content-container">
                        <div class="section-title-left-wrap">
                            <h2 class="section-title-left">ПРИЗЫ</h2>
                        </div>

                        <div class="prizes-cards-grid">
                            <!-- Card 1 -->
                            <div class="prize-card">
                                <div class="prize-image-placeholder">
                                    <div class="prize-placeholder-inner">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="prize-icon">
                                            <circle cx="12" cy="8" r="7"></circle>
                                            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
                                        </svg>
                                    </div>
                                </div>
                                <p class="prize-card-title">СУДЕЙСТВО НА ФИНАЛЕ</p>
                            </div>

                            <!-- Card 2 -->
                            <div class="prize-card">
                                <div class="prize-image-placeholder">
                                    <div class="prize-placeholder-inner">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="prize-icon">
                                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                                            <line x1="3" y1="6" x2="21" y2="6"></line>
                                            <path d="M16 10a4 4 0 0 1-8 0"></path>
                                        </svg>
                                    </div>
                                </div>
                                <p class="prize-card-title">ЯЩИК JAMESON TRIPLE TRIPLE</p>
                            </div>

                            <!-- Card 3 -->
                            <div class="prize-card">
                                <div class="prize-image-placeholder">
                                    <div class="prize-placeholder-inner">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#FEF8E1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="prize-icon">
                                            <polyline points="20 12 20 22 4 22 4 12"></polyline>
                                            <rect x="2" y="7" width="20" height="5"></rect>
                                            <line x1="12" y1="22" x2="12" y2="7"></line>
                                            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
                                            <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
                                        </svg>
                                    </div>
                                </div>
                                <p class="prize-card-title">НАБОР МЕРЧА</p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- BLOCK 5 (ENTHUSIAST): ФОРМА ЗАЯВКИ (2 Columns: Form & Bottles) -->
                <section class="block-enthusiast-form-section">
                    <div class="landing-content-container">
                        <div class="form-two-columns-layout">
                            <!-- LEFT COLUMN: FORM -->
                            <div class="form-column-left">
                                <div v-if="isLoadingForm" class="form-loading-wrap">
                                    <v-progress-circular indeterminate size="48" color="#FEF8E1" />
                                </div>
                                <div v-else-if="formError" class="form-error-wrap">
                                    <p class="form-error-text">Ошибка загрузки формы. Попробуйте позже.</p>
                                    <button type="button" class="ghost-action-btn clear-btn mt-3" @click="getFormFields(currentCampaignId)">
                                        Повторить попытку
                                    </button>
                                </div>
                                <form v-else @submit.prevent="confirmRegistration" class="punch-entry-form">
                                    <!-- СЕКЦИЯ 1: ВАШИ ДАННЫЕ -->
                                    <div class="enthusiast-form-section" v-if="enthusiastSections.userData.length > 0">
                                        <h3 class="enthusiast-section-title">ВАШИ ДАННЫЕ</h3>
                                        <div class="enthusiast-fields-stack">
                                            <div
                                                v-for="question in enthusiastSections.userData"
                                                :key="question.id"
                                                class="field-item"
                                            >
                                                <label class="field-label">{{ question.name }}*</label>

                                                <!-- Телефон (4) -->
                                                <input
                                                    v-if="question.type === 4"
                                                    type="tel"
                                                    class="field-input"
                                                    placeholder="+7 (___) ___-__-__"
                                                    v-mask="'+7(###) ###-##-##'"
                                                    v-model="formData[question.id]"
                                                    @focus="onPhoneFocus(question)"
                                                    @input="onPhoneInput(question)"
                                                    @blur="markTouched(question)"
                                                />

                                                <!-- Выпадающий список (если есть) -->
                                                <v-select
                                                    v-else-if="isSelect(question)"
                                                    v-model="formData[question.id]"
                                                    :items="question.answers"
                                                    item-text="name"
                                                    item-value="id"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :placeholder="getPlaceholder(question)"
                                                    class="custom-v-select"
                                                    :menu-props="{ bottom: true, offsetY: true, contentClass: 'punch-select-menu' }"
                                                    @change="validateField(question)"
                                                    @blur="markTouched(question)"
                                                >
                                                    <template v-slot:selection="{ item }">
                                                        <div class="select-selected-text">{{ item ? item.name : getPlaceholder(question) }}</div>
                                                    </template>
                                                </v-select>

                                                <!-- Обычный ввод (Имя, Фамилия, Email) -->
                                                <input
                                                    v-else
                                                    :type="question.type === 7 ? 'email' : 'text'"
                                                    class="field-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />

                                                <span v-if="errors[question.id]" class="field-error-msg">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 2: ВАШ РЕЦЕПТ -->
                                    <div class="enthusiast-form-section" v-if="enthusiastSections.userRecipe.length > 0">
                                        <h3 class="enthusiast-section-title">ВАШ РЕЦЕПТ</h3>
                                        <div class="enthusiast-fields-stack">
                                            <div
                                                v-for="question in enthusiastSections.userRecipe"
                                                :key="question.id"
                                                class="field-item"
                                            >
                                                <label class="field-label">{{ question.name }}*</label>

                                                <!-- Описание и ингредиенты (textarea, 4 строки) -->
                                                <div class="textarea-wrapper" v-if="isTextarea(question)">
                                                    <textarea
                                                        class="field-input field-textarea"
                                                        :placeholder="getPlaceholder(question)"
                                                        v-model="formData[question.id]"
                                                        @input="validateField(question)"
                                                        @blur="markTouched(question)"
                                                        maxlength="800"
                                                        rows="4"
                                                    ></textarea>
                                                    <div class="char-counter" :class="{ 'char-counter-limit': (formData[question.id] || '').length >= 800 }">
                                                        {{ (formData[question.id] || '').length }} / 800
                                                    </div>
                                                </div>

                                                <!-- Название пунша -->
                                                <input
                                                    v-else
                                                    type="text"
                                                    class="field-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />

                                                <span v-if="errors[question.id]" class="field-error-msg">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- ДОПОЛНИТЕЛЬНЫЕ ПОЛЯ (ЕСЛИ ЕСТЬ НЕРАСПРЕДЕЛЕННЫЕ) -->
                                    <div class="enthusiast-form-section" v-if="enthusiastSections.extra.length > 0">
                                        <div class="enthusiast-fields-stack">
                                            <div
                                                v-for="question in enthusiastSections.extra"
                                                :key="question.id"
                                                class="field-item"
                                            >
                                                <v-select
                                                    v-if="isSelect(question)"
                                                    v-model="formData[question.id]"
                                                    :items="question.answers"
                                                    item-text="name"
                                                    item-value="id"
                                                    dense
                                                    outlined
                                                    hide-details
                                                    :placeholder="getPlaceholder(question)"
                                                    class="custom-v-select"
                                                    :menu-props="{ bottom: true, offsetY: true, contentClass: 'punch-select-menu' }"
                                                    @change="validateField(question)"
                                                    @blur="markTouched(question)"
                                                >
                                                    <template v-slot:selection="{ item }">
                                                        <div class="select-selected-text">{{ item ? item.name : getPlaceholder(question) }}</div>
                                                    </template>
                                                </v-select>

                                                <div class="textarea-wrapper" v-else-if="isTextarea(question)">
                                                    <textarea
                                                        class="field-input field-textarea"
                                                        :placeholder="getPlaceholder(question)"
                                                        v-model="formData[question.id]"
                                                        @input="validateField(question)"
                                                        @blur="markTouched(question)"
                                                        maxlength="800"
                                                        rows="4"
                                                    ></textarea>
                                                    <div class="char-counter" :class="{ 'char-counter-limit': (formData[question.id] || '').length >= 800 }">
                                                        {{ (formData[question.id] || '').length }} / 800
                                                    </div>
                                                </div>

                                                <input
                                                    v-else
                                                    type="text"
                                                    class="field-input"
                                                    :placeholder="getPlaceholder(question)"
                                                    v-model="formData[question.id]"
                                                    @input="validateField(question)"
                                                    @blur="markTouched(question)"
                                                />

                                                <span v-if="errors[question.id]" class="field-error-msg">{{ errors[question.id] }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 3: ЧЕКБОКСЫ (БЕЗ ЗАГОЛОВКА) -->
                                    <div class="form-checkboxes-block" v-if="enthusiastSections.consents.length > 0">
                                        <div v-for="(question, idx) in enthusiastSections.consents" :key="question.id" class="checkbox-wrapper">
                                            <div class="checkbox-row-item">
                                                <input
                                                    type="checkbox"
                                                    :id="'enthusiast-chk-' + question.id"
                                                    class="custom-check-input"
                                                    v-model="formData[question.id]"
                                                    @change="markTouched(question)"
                                                />
                                                <label :for="'enthusiast-chk-' + question.id" class="custom-check-label">
                                                    <template v-if="getCheckboxInfo(question, idx).isSpecial">
                                                        {{ getCheckboxInfo(question, idx).prefix }}
                                                        <a class="policy-link-inline" @click.stop.prevent="openPopup(getCheckboxInfo(question, idx).popup)">
                                                            {{ getCheckboxInfo(question, idx).linkText }}
                                                        </a>
                                                    </template>
                                                    <template v-else>
                                                        {{ getCheckboxInfo(question, idx).text }}
                                                    </template>
                                                </label>
                                            </div>
                                            <span v-if="errors[question.id]" class="field-error-msg">{{ errors[question.id] }}</span>
                                        </div>
                                    </div>

                                    <!-- СЕКЦИЯ 4: КНОПКИ (В САМОМ НИЗУ) -->
                                    <div class="form-buttons-row">
                                        <button type="submit" class="enthusiast-btn-submit" :disabled="submitting || isSendSms">
                                            <span v-if="!submitting && !isSendSms">ПОДЕЛИТЬСЯ РЕЦЕПТОМ</span>
                                            <span v-else>ОТПРАВКА...</span>
                                        </button>
                                        <button type="button" class="enthusiast-btn-clear" @click="handleClearCurrentForm">
                                            ОЧИСТИТЬ
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <!-- RIGHT COLUMN: PUNCH IMAGE (Без рамок, фонов и свечений) -->
                            <div class="enthusiast-image-column">
                                <img src="@/assets/img/form-punch-image.png" alt="Jameson Punch" class="enthusiast-form-image" />
                            </div>
                        </div>
                    </div>
                </section>
            </template>

            <!-- ============================================================= -->
            <!-- BLOCK 10 (COMMON): ФУТЕР                                      -->
            <!-- ============================================================= -->
            <footer class="block-footer-section">
                <div class="footer-centered-container">
                    <!-- Centered Serif Jameson Logo -->
                    <div class="footer-serif-logo-wrap">
                        <div class="hero-logo-wrapper">
                            <img src="@/assets/img/jameson-logo-age-confirm.png" alt="JAMESON" class="jameson-white-logo" />
                        </div>
                    </div>

                    <!-- 3 Lines of text (opacity 0.8) -->
                    <div class="footer-text-lines">
                        <p class="footer-line">Jameson Punch Competition 2026</p>
                        <p class="footer-line">Дедлайн: 10 сентября 2026, 23:59</p>
                        <p class="footer-line">
                            Вопросы? Напишите нам в
                            <a href="https://www.instagram.com/jamesonkz" target="_blank" rel="noopener" class="footer-instagram-link">@JamesonKZ</a>
                            в Instagram
                        </p>
                    </div>

                    <!-- Регламент (только для барменов) -->
                    <div v-if="selectedRole === 'bartender'" class="footer-reglament-wrap">
                        <a href="#" class="footer-reglament-link" @click.prevent="openPopup('reglament')">
                            <svg class="footer-reglament-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            <span>Подробный регламент</span>
                        </a>
                    </div>

                    <!-- Legal disclaimer -->
                    <p class="footer-disclaimer-small">
                        Чрезмерное употребление алкоголя вредит вашему здоровью. Конкурс предназначен исключительно для лиц старше 21 года.
                    </p>
                </div>
            </footer>
        </div>

        <!-- ================================================================= -->
        <!-- OTP CONFIRMATION POPUP                                            -->
        <!-- ================================================================= -->
        <Otp
            :key="'otp-' + (selectedRole || 'default')"
            :show-popup="showOtpPopup"
            :sms-timer="smsTimer"
            :loading="otpVerifying"
            :success="otpSuccess"
            :error-sms="otpErrorSms"
            :error-message="otpError"
            @verifyCode="submitAnswersWithCode"
            @resend="resendSms"
            @closePopup="closeOtpPopup"
        />

        <!-- ================================================================= -->
        <!-- POLICY & RULES MODAL                                              -->
        <!-- ================================================================= -->
        <transition name="popup">
            <div v-if="isPopupOpen" class="popup-overlay" @click.self="closePopup">
                <div class="popup-content">
                    <div class="popup-header">
                        <div class="header-text">{{ currentPopupData.title }}</div>
                        <div class="popup-close-1" @click="closePopup">
                            <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.494853 15.703C-0.144973 16.3298 -0.158031 17.4789 0.50791 18.1448C1.17385 18.8108 2.32293 18.7847 2.94969 18.1579L9.00845 12.0991L15.0672 18.1448C15.7201 18.7977 16.8431 18.8108 17.509 18.1318C18.1749 17.4658 18.1749 16.3429 17.5221 15.69L11.4764 9.64428L17.5221 3.58553C18.1749 2.93264 18.1749 1.80968 17.509 1.14374C16.8431 0.464742 15.7201 0.4778 15.0672 1.13068L9.00845 7.17638L2.94969 1.11763C2.32293 0.490858 1.17385 0.464742 0.50791 1.13068C-0.158031 1.79662 -0.144973 2.9457 0.494853 3.58553L6.55361 9.64428L0.494853 15.703Z" fill="#F2E4B5" />
                            </svg>
                        </div>
                    </div>
                    <div class="popup-text-1" v-html="currentPopupData.content"></div>
                </div>
            </div>
        </transition>

        <!-- ================================================================= -->
        <!-- NOTIFICATION SNACKBAR                                             -->
        <!-- ================================================================= -->
        <v-snackbar v-model="snackbar" timeout="3500">
            {{ snackbarMessage }}
            <template v-slot:action="{ attrs }">
                <div class="snackbar-btn" v-bind="attrs" @click="snackbar = false">
                    Закрыть
                </div>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mask } from "vue-the-mask";
import Otp from "@/components/otp.vue";
import ConfirmAge1 from "@/components/age-confirm.vue";
import {
    initLanding,
    trackFormStart,
    trackFormSubmit,
    trackFormError,
    trackRegistrationSuccess,
    trackVirtualPageView
} from '@/utils/analytics';

export default {
    name: "PunchCompetition",
    directives: { mask },
    components: {
        Otp,
        ConfirmAge1
    },
    data() {
        return {
            campaignIds: {
                enthusiast: "ac8d536e-85a0-f111-b802-00155d79212a",
                bartender: "41be4ee1-83a0-f111-b802-00155d79212a"
            },
            isMobile: window.innerWidth < 1264,
            showAgeConfirm: true,
            selectedRole: null, // Always null on page load so role is asked after Age Gate
            showRoleModal: false,
            showBartenderForm: false, // Hidden until "ПОДАТЬ ЗАЯВКУ" is clicked

            // Dynamic form state
            formSchema: null,
            formData: {},
            errors: {},
            touched: {},
            isLoadingForm: false,
            formError: false,
            submitting: false,
            isSendSms: false,

            // Countdown timer state (Target: 7 сентября 2026, 23:59:59)
            targetDeadline: new Date("2026-09-10T23:59:59+05:00").getTime(),
            countdownInterval: null,
            timerDays: 12,
            timerHours: 8,
            timerMinutes: 34,
            timerSeconds: 15,

            // FAQ Data for Bartender Role
            bartenderFaqs: [
                {
                    q: "КАК ПОДАТЬ ЗАЯВКУ?",
                    a: "Заполните форму заявки и отправьте все требуемые данные: название пунша, концепцию, полный рецепт с пропорциями и ссылку на ваш Instagram пост.",
                    isOpen: true
                },
                {
                    q: "КАКОЙ РЕЛИЗ JAMESON ИСПОЛЬЗОВАТЬ?",
                    a: "Вы можете использовать любой релиз Jameson из доступных в РК: Original, Black Barrel, Crested, Orange, Caskmates и другие.",
                    isOpen: false
                },
                {
                    q: "РЕЦЕПТ ДОЛЖЕН БЫТЬ ОРИГИНАЛЬНЫМ?",
                    a: "Да, рецепт должен быть создан специально для этого конкурса и не публиковаться в открытых источниках до подачи заявки.",
                    isOpen: false
                },
                {
                    q: "СКОЛЬКО ЗАЯВОК Я МОГУ ПОДАТЬ?",
                    a: "Каждый участник может подать только одну заявку. Количество участников от одного заведения не ограничивается.",
                    isOpen: false
                },
                {
                    q: "КАК УЗНАТЬ РЕЗУЛЬТАТЫ?",
                    a: "Результаты публикуются в @JamesonKZ. ТОП 10 объявляется 10 сентября, ТОП 5 финалистов — 14 сентября.",
                    isOpen: false
                },
                {
                    q: "ЧТО ЕСЛИ Я НЕ ПРОШЕЛ ОТБОР?",
                    a: "Жюри предоставит краткие комментарии. Рекомендуем сохранить рецепт для использования в своем баре и развития для следующего конкурса.",
                    isOpen: false
                },
                {
                    q: "КАК ПОДГОТОВИТЬСЯ К ФИНАЛУ?",
                    a: "Репетируйте приготовление пунша на 45 человек. Финал — это демонстрация вашего мастерства под давлением.",
                    isOpen: false
                }
            ],

            // OTP state
            showOtpPopup: false,
            otpVerifying: false,
            otpSuccess: false,
            otpErrorSms: false,
            otpError: "",
            smsTimer: 60,
            smsTimerInterval: null,

            // Info popups & Snackbar
            isPopupOpen: false,
            currentPopup: "policy",
            snackbar: false,
            snackbarMessage: "",

            popupTexts: {
                policy: {
                    title: "Политика конфиденциальности",
                    content: `<b>Pernod Ricard Kazakhstan,</b> в качестве оператора персональных данных, уважает ваше
право на конфиденциальность при использовании веб-сайта
Цель настоящей Политики конфиденциальности — информировать вас о том, каким
образом компания Pernod Ricard Kazakhstan собирает, хранит, использует и обрабатывает
ваши персональные данные, предоставленные через веб-сайт (далее «Веб-сайт»).
Темы:
1. Какие персональные данные мы собираем?
2. Как и зачем мы используем технологии отслеживания?
3. С какой целью мы используем ваши персональные данные и на каком правовом
основании?
4. Что произойдет, если вы откажетесь предоставить свои персональные данные?
5. Кому мы раскрываем ваши персональные данные и почему?
6. Передаются ли ваши персональные данные за пределы Казахстана?
7. Как долго мы храним ваши персональные данные?
8. Как мы обеспечиваем защиту ваших персональных данных?
9. Каковы ваши права в отношении ваших персональных данных?
10. Как с нами связаться или подать жалобу в надзорный орган?
11. Изменения в Политике конфиденциальности

<b>1. Какие персональные данные мы собираем?</b>
Мы собираем следующие персональные данные:
- Имя, фамилия
- Дата рождения
- Адрес электронной почты
- Пароль
- Должность
- Название места работы
- Фотография
- IP-адрес
- Интересы
<b>Примечание:</b> IP-адрес — это последовательность чисел, используемая для
идентификации вашего устройства при каждом подключении к интернету.

<b>2. Как и зачем мы используем технологии отслеживания?</b>
С вашего согласия, если это требуется по закону, наш Веб-сайт использует файлы cookie и
подобные технологии отслеживания, такие как IP-адреса или журналы. Эти технологии
помогают нам:
- Лучше понимать, как посетители используют наш Веб-сайт и его функции.
- Улучшать их функциональность.
- Предоставлять контент, соответствующий вашим интересам.

<b>Подробнее о cookies:</b> см. Политику использования файлов cookie.

<b>3. С какой целью мы используем ваши персональные данные и на каком правовом основании?</b>
<b>Pernod Ricard Kazakhstan</b> обрабатывает ваши данные в следующих целях:
- Предоставление доступа к Веб-сайту и создание профиля пользователя.
- Обеспечение доступа к функциям сайта (например, доступ к онлайн-контенту,
участие в тренингах, регистрация на мероприятия).
- Отправка административных уведомлений и сервисных сообщений.
Мы также можем адаптировать наш контент в зависимости от ваших интересов для
улучшения вашего опыта на сайте.
При наличии вашего согласия данные могут использоваться для отправки маркетинговых
сообщений.

<b>4. Что произойдет, если вы откажетесь предоставить свои персональные данные?</b>
Если вы решите не предоставлять запрашиваемые данные, вы не сможете:
- Участвовать в некоторых активностях.
- Пользоваться персонализированными функциями.
- Получать специальные услуги.

<b>5. Кому мы раскрываем ваши персональные данные и почему?</b>
<b>В рамках группы Pernod Ricard</b>
Ваши данные могут быть переданы аффилированным компаниям группы Pernod Ricard.
<b>Третьи стороны</b>
Ваши данные могут быть переданы третьим сторонам в следующих случаях:
- <b>Техническая поддержка:</b> провайдеры, агенты или подрядчики.
- <b>Организация мероприятий и тренингов.</b>
- <b>По закону:</b> для защиты прав и безопасности компании или общества.
- <b>Слияние или поглощение:</b> передача данных новому владельцу в соответствии с
законом.

<b>6. Передаются ли ваши персональные данные за пределы Казахстана?</b>
Данные могут передаваться за границу с соблюдением всех необходимых мер защиты,
включая стандартные договорные положения Европейской комиссии.

<b>7. Как долго мы храним ваши персональные данные?</b>
Данные хранятся:
- Пока ваш аккаунт активен.
- Пока это необходимо для предоставления функций сайта.
Также данные могут храниться для выполнения правовых обязательств.

<b>8. Как мы обеспечиваем защиту ваших персональных данных?</b>
<b>Pernod Ricard Kazakhstan</b> принимает все необходимые меры для защиты ваших данных,
включая:
- Хранение данных в защищенных операционных средах.
- Доступ к данным только у уполномоченных сотрудников и подрядчиков.

<b>9. Каковы ваши права в отношении ваших персональных данных?</b>
Вы имеете право:
- Отозвать согласие на обработку данных.
- Запросить доступ, исправление или удаление данных.
- Ограничить обработку или запросить перенос данных.
- Возражать против обработки.

<b>10. Как с нами связаться или подать жалобу в надзорный орган?</b>
По вопросам и жалобам, связанным с Политикой конфиденциальности, обращайтесь по
адресу:
<b>Алматы, Казахстан, Рубинштейна, 48, 050060</b>

Или по электронной почте: <b>promo.kz@pernod-ricard.com.</b>

<b>11. Изменения в Политике конфиденциальности</b>
Мы регулярно обновляем нашу Политику конфиденциальности и размещаем изменения
на этой странице.
<b>Дата последнего обновления:</b> ноябрь 2024 года.`
                },
                offer: {
                    title: "СОГЛАСИЕ НА СБОР И ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ",
                    content: `Я подтверждаю, что достиг возраста 21 года и более и даю <b>ТОО «Перно Рикар
Казахстан», (БИН 050840001045),</b> а также их работникам и третьим лицам-соисполнителям по
заключенным между ними договорам, <b>свое согласие</b> на сбор, обработку,
распространение и трансграничную передачу (без дополнительных уведомлений)
моих персональных данных, относящихся исключительно к перечисленным ниже
категориям персональных данных:
 1) ФИО
 2) Город проживания
 3) Мобильный телефон
 4) E-mail
<b>Согласованная цель сбора и обработки персональных данных</b> – пополнение CRM
базы посредством предоставления персональных данных любыми средствами связи
для участия в активностях, проводимых в отношении товарных знаков продукции
ТОО «Перно Рикар Казахстан».
Я даю согласие на использование персональных данных исключительно в
вышеуказанных целях, а также на обработку персональных данных и их
трансграничную передачу на всех электронных носителях, используемых для
вышеуказанной цели.
Настоящее согласие предоставляется мной на осуществление действий в отношении
моих персональных данных, которые необходимы для достижения указанных выше
целей, включая (без ограничения) сбор, систематизацию, накопление, хранение,
точнение (обновление, изменение), использование, передачу третьим лицам для
осуществления действий по обмену информацией, обезличивание, блокирование
персональных данных, трансграничную передачу персональных данных на территорию любого государства, в том числе не обеспечивающего защиту персональных данных, распространение моих персональных данных в общедоступных источниках,
а также осуществление любых иных действий,
предусмотренных действующим законодательством Республики Казахстан.
Я предоставляю согласие на контактирование со мной для вышеуказанных целей путем осуществления разговора, телефонных звонков, направления мне уведомлений с использованием почты, телефона (также SMS), социальных сетей и/или мессенджеров, вебсайтов, электронной почты, электронных информационных ресурсов и иных информационных систем и мобильных приложений. 
Я проинформирован, что вышеуказанные компании гарантируют обработку моих
персональных данных в соответствии с действующим законодательством Республики
Казахстан как неавтоматизированным, так и автоматизированным способами.
Данное согласие действует в течение срока хранения информации в вышеуказанных
целях.
Данное согласие может быть отозвано в любой момент по моему письменному заявлению, отправленному в адрес Компании по ее юридическому адресу, зарегистрированному на дату отправки заявления или посредством направления электронного сообщения на электронную почту Компании: promo.kz@pernod-ricard.com. В случае отзыва настоящего согласия все мои Персональные данные подлежат уничтожению из базы данных Компании. 
Я подтверждаю, что для включения моих персональных данных в базу данных, в том числе партнеров указанных компаний, передачи, распространения, дополнения и прочей обработки моих персональных данных указанными компаниями с вышеупомянутой целью не требуется дополнительного уведомления меня о совершении данных действий. 

Я подтверждаю, что, давая настоящее согласие, я действую по собственной воле, в своих интересах и полностью осознаю суть и цели подписываемого документа.`
                },
                reglament: {
    title: "РЕГЛАМЕНТ ПРОВЕДЕНИЯ КОНКУРСА",
    content: `          <b>1. Требования к участникам</b>

                    <b>1.1. Общие требования</b>
                    1.1.1. К участию в Конкурсе допускаются дееспособные физические лица, достигшие возраста 21 (двадцати одного) года на дату подачи заявки.
                    1.1.2. Участником Конкурса может являться исключительно лицо, являющееся резидентом Республики Казахстан и постоянно проживающее на территории Республики Казахстан.

                    <b>1.2. Требования к профессиональным участникам</b>
                    1.2.1. К участию в Конкурсе в качестве профессионального участника допускаются действующие сотрудники бара, ресторана или иного предприятия общественного питания, имеющие непосредственное отношение к приготовлению алкогольных напитков, в том числе барбэки, бармены, бар-менеджеры и шеф-бармены.
                    1.2.2. Заявка на участие в качестве профессионального участника подаётся одним физическим лицом. Количество профессиональных участников от одного бара, ресторана или иного предприятия общественного питания не ограничивается.

                    <b>1.3. Требования к потребителям</b>
                    1.3.1. К участию в Конкурсе в качестве потребителя допускаются лица, не осуществляющие профессиональную деятельность в барах, ресторанах или иных предприятиях общественного питания, связанную с приготовлением алкогольных напитков.
                    1.3.2. Один потребитель вправе подать только одну заявку.

                    <b>1.4. Общие положения</b>
                    1.4.1. Организатор вправе запросить у участника документы и/или иные сведения, подтверждающие соответствие требованиям, установленным настоящим Регламентом.
                    1.4.2. К участию в Конкурсе не допускаются сотрудники Организатора, члены жюри Конкурса, а также лица, имеющие конфликт интересов, способный повлиять на объективность проведения или результаты Конкурса.
                    1.4.3. Для участия в Конкурсе участник должен иметь действующий паспорт, пригодный для выезда за пределы Республики Казахстан, срок действия которого составляет не менее 6 (шести) месяцев с даты предполагаемого выезда в поездку, предусмотренную в качестве главного приза Конкурса.

                    <b>2. Требования к рецепту и продукту</b>

                    2.1. Участник обязан разработать авторский пунш на основе продукции Jameson, при этом содержание продукции Jameson в рецептуре должно составлять не менее 100 (ста) миллилитров.
                    2.2. В Конкурсе допускается использование следующих релизов Jameson, доступных для приобретения на территории Республики Казахстан:
                    - Jameson Original;
                    - Jameson Black Barrel;
                    - Jameson Crested;
                    - Jameson Orange;
                    - Jameson Caskmates IPA Edition;
                    - Jameson Caskmates Stout Edition.
                    2.3. Общий объём готового напитка должен составлять не менее 600 (шестисот) миллилитров без учёта льда.
                    2.4. Для целей настоящего Регламента под пуншем понимается напиток, предназначенный для совместного потребления несколькими людьми и приготовленный в увеличенном объёме. В качестве рекомендуемой структуры пунша могут использоваться сладкая, кислая, пряная и разбавляющая составляющие, однако конечный состав, пропорции и способ приготовления определяются участником самостоятельно.
                    2.5. Количество и состав ингредиентов, используемых в рецептуре, не ограничиваются, за исключением требований, установленных настоящим Регламентом.
                    2.6. Допускается использование алкогольных ингредиентов, входящих в актуальный портфель Pernod Ricard.
                    2.7. Если необходимая участнику категория алкогольной продукции отсутствует в портфеле Pernod Ricard, допускается использование продукции других производителей соответствующей категории. В таком случае в заявке указывается исключительно категория алкогольного продукта без указания его бренда или производителя.
                    2.8. Требования, установленные пунктами 2.6 и 2.7 настоящего Регламента, распространяются на все этапы Конкурса, включая подачу заявки, отбор конкурсных работ и финальный этап.
                    2.9. Все ингредиенты, используемые непосредственно в рецептуре, а также ингредиенты, входящие в состав любых самостоятельно приготовленных заготовок, должны быть доступны для приобретения в розничной продаже на территории Республики Казахстан.
                    2.10. Рецептура должна быть воспроизводима в домашних условиях и не должна требовать использования специализированного профессионального или лабораторного оборудования либо сложных лабораторных техник.
                    2.11. Допускается использование самостоятельно приготовленных заготовок, включая, но не ограничиваясь, сиропами, кордиалами, инфьюзами и аналогичными компонентами, при условии соблюдения требований настоящего Регламента.
                    2.12. При использовании самостоятельно приготовленной заготовки участник обязан предоставить её полный рецепт с указанием всех используемых ингредиентов, их количества и подробного способа приготовления.
                    2.13. Рецепт каждой самостоятельно приготовленной заготовки должен быть воспроизводим в домашних условиях с использованием ингредиентов, доступных для приобретения в розничной продаже на территории Республики Казахстан.
                    2.14. Допускается использование безалкогольных компонентов, включая воду, соки, чай, газированрованные напитки и иные аналогичные продукты, при условии соблюдения требований настоящего Регламента.
                    2.15. Рецепт должен быть создан участником специально для участия в Конкурсе. Не допускается участие рецепта, который ранее участвовал в других конкурсах или был опубликован участником в открытых источниках до даты подачи заявки на участие в настоящем Конкурсе.
                    2.16. Рецептура должна быть пригодна для масштабирования до объёма, необходимого для приготовления пунша на финальном этапе Конкурса.

                    <b>3. Требования к заявке потребителя</b>

                    3.1. Для участия в Конкурсе потребителю необходимо направить рецепт пунша на основе Jameson в прямые сообщения (Direct) официального Instagram-аккаунта JamesonKZ в период проведения Конкурса.
                    3.2. Рецепт направляется в свободной форме. Участнику не требуется использовать установленный формат оформления заявки или предоставлять фотографию готового напитка.
                    3.3. В заявке должна быть указана информация, позволяющая воспроизвести предложенный рецепт, включая перечень ингредиентов и их количество.
                    3.4. Рецепт должен соответствовать общим требованиям к рецепту и продукту, установленным настоящим Регламентом.
                    3.5. От одного участника принимается одна заявка.
                    3.6. Направляя заявку, участник подтверждает свое согласие с условиями настоящего Регламента.

                    <b>4. Требования к заявке профессионального участника</b>

                    4.1. Для участия в Конкурсе профессиональный участник обязан заполнить и направить заявку через форму, размещенную на официальном сайте Конкурса, в срок, установленный Организатором. 
                    4.2. Заявка профессионального участника должна содержать:
                    а) название пунша;
                    б) концепцию пунша;
                    в) полный перечень ингредиентов с указанием точного количества каждого ингредиента;
                    г) полный рецепт и описание техники приготовления;
                    д) рецепты и описание всех самостоятельно приготовленных заготовок, используемых в составе пунша, с указанием всех ингредиентов, их количества и способа приготовления.
                    4.3. В рамках подачи заявки профессиональный участник обязан опубликовать пост в своём аккаунте Instagram, содержащий:
                    - фотографию конкурсного пунша;
                    - визуальное присутствие бутылки используемого релиза Jameson;
                    - отметки аккаунтов, указанных Организатором;
                    - установленный хештег Конкурса #jamesonpunchcompetition;
                    - название, концепцию и описание конкурсного пунша.
                    4.4. Подача заявки осуществляется путем заполнения формы на официальном сайте Конкурса. Адрес официального сайта Конкурса, сроки подачи заявок и иные технические требования доводятся до сведения участников через официальные каналы коммуникации Конкурса. 
                    4.5. От одного профессионального участника принимается одна заявка.
                    4.6. Один бар, ресторан или иное предприятие общественного питания вправе представить неограниченное количество участников, при этом каждый участник подаёт самостоятельную заявку.
                    4.7. Неполное выполнение требований к заявке может являться основанием для отклонения заявки без дополнительного уведомления участника.

                    <b>5. Отбор участников среди потребителей</b>

                    5.1. По окончании срока приёма заявок Организатор осуществляет предварительный отбор поступивших рецептов и формирует список из 3 (трёх) лучших рецептов.
                    5.2. При предварительном отборе учитываются следующие критерии:
                    - оригинальность рецепта;
                    - простота приготовления и воспроизводимость в домашних условиях;
                    - вкусовой потенциал рецепта.
                    5.3. Отобранные 3 (три) рецепта готовятся Организатором для создания видеоконтента, который публикуется в официальном Instagram-аккаунте JamesonKZ.
                    5.4. При подготовке видеоконтента Организатор вправе самостоятельно определить формат приготовления, подачи и съёмки конкурсных пуншей.
                    5.5. Для определения победителя среди потребителей конкурсные пунши оцениваются судьями в рамках подготовки видеоконтента.
                    5.6. При определении победителя учитываются следующие критерии:
                    - вкус;
                    - оригинальность;
                    - воспроизводимость рецепта в домашних условиях.
                    5.7. По итогам оценки определяется 1 (один) победитель среди потребителей.
                    5.8. Победитель объявляется посредством публикации на официальном Instagram-аккаунте JamesonKZ.
                    5.9. Победитель потребительского конкурса получает право принять участие в качестве судьи в финальном этапе профессионального Конкурса наравне с другими членами жюри, а также получает специальные призы от бренда Jameson — лимитированный набор мерча и 6 (шесть) бутылок Jameson Triple Triple.

                    <b>6. Онлайн-отбор профессиональных участников</b>

                    6.1. После завершения приёма заявок жюри Конкурса осуществляет оценку заявок профессиональных участников и формирует список из 10 (десяти) участников, набравших наибольшее количество баллов.
                    6.2. Оценка конкурсных работ осуществляется членами жюри по бальной системе. Каждый член жюри выставляет оценки по каждому критерию по шкале от 0 (нуля) до 10 (десяти) баллов.

                    <b>6.3. Критерии оценки онлайн-отбора</b>
                    <b>6.3.1. Интеграция бренда Jameson</b>
                    Оценивается степень соответствия конкурсной работы философии бренда Jameson, включая, но не ограничиваясь: идеей совместного потребления (shared experience), открытостью, доступностью, дружелюбной атмосферой и комьюнити-ориентированностью.
                    При выставлении оценки учитываются:
                    - органичность включения Jameson в рецептуру и концепцию;
                    - наличие визуального присутствия бренда в подаче и коммуникации;
                    - соответствие общей идеи заявки ценностям бренда, в том числе духу открытости, простоты и объединения людей, транслируемым в коммуникациях платформы BEAT.
                    Оценка по критерию:
                    - 0 баллов — бренд не интегрирован, отсутствует в концепции и/или визуальной части, идея заявки противоречит философии бренда;
                    - 1–4 балла — формальное присутствие бренда без глубокой интеграции в идею;
                    - 5–7 баллов — бренд интегрирован в рецептуру и частично в концепцию;
                    - 8–10 баллов — бренд органично встроен в рецепт, подачу и идею, полностью соответствует философии и усиливает её.

                    <b>6.3.2. Рецептура и баланс ингредиентов</b>
                    Оцениваются вкусовые характеристики напитка и профессиональный уровень работы с ингредиентами.
                    При выставлении оценки учитываются:
                    - баланс вкуса (сладость, кислотность, горечь, алкогольность);
                    - гармоничность сочетания ингредиентов;
                    - уместность выбранных компонентов;
                    - соответствие формату пунша (напиток для совместного потребления);
                    - воспроизводимость рецепта.
                    Оценка по критерию:
                    - 0 баллов — рецепт нерабочий или непригодный к употреблению;
                    - 1–4 балла — выраженный дисбаланс, ошибки в сочетании ингредиентов;
                    - 5–7 баллов — корректный, сбалансированный напиток без выраженной уникальности;
                    - 8–10 баллов — высокий уровень баланса, продуманная рецептура, цельный и запоминающийся вкус.

                    <b>6.3.3. Креатив и качество визуальной коммуникации</b>
                    Оценивается фотография конкурсного пунша, опубликованная участником, как основной инструмент визуальной презентации конкурсной работы.
                    При выставлении оценки учитываются:
                    - качество фотографии (резкость, свет, композиция, читаемость кадра);
                    - наличие и корректное визуальное присутствие бутылки Jameson;
                    - соответствие изображения заявленной концепции напитка;
                    - креативность визуального решения и сторителлинг через кадр;
                    - способность фотографии передать атмосферу и идею совместного потребления;
                    - общее визуальное впечатление и привлекательность изображения.
                    Оценка по критерию:
                    - 0 баллов — фотография не соответствует требованиям или не позволяет оценить продукт;
                    - 1–4 балла — низкое качество изображения, слабая композиция, отсутствие идеи;
                    - 5–7 баллов — корректная, технически приемлемая фотография без ярко выраженной концепции;
                    - 8–10 баллов — сильное визуальное решение, качественная и продуманная фотография, усиливающая концепцию и привлекающая внимание.

                    6.4. Максимальное количество баллов, которое может выставить один член жюри, составляет 30 (тридцать) баллов.
                    6.5. Итоговая оценка участника формируется путём суммирования баллов, выставленных тремя членами жюри. Максимально возможный суммарный балл составляет 90 (девяносто) баллов.
                    6.6. В случае равенства итоговых баллов у двух и более участников преимущество отдаётся участнику, набравшему наибольшее количество баллов по критерию «Интеграция бренда Jameson». В случае повторного равенства решение принимается коллегиально членами жюри.
                    6.7. По результатам онлайн-отбора формируется список из 10 (десяти) профессиональных участников, которые переходят на следующий этап Конкурса.
                    6.8. Результаты онлайн-отбора могут быть опубликованы на официальных ресурсах Конкурса.

                    <b>7. Отбор TOP 5 профессиональных участников</b>

                    7.1. После завершения онлайн-отбора 10 (десять) участников, набравших наибольшее количество баллов, проходят на следующий этап Конкурса.
                    7.2. На данном этапе Организатор самостоятельно готовит конкурсные пунши каждого из 10 (десяти) участников в соответствии с рецептами, указанными в поданных заявках.
                    7.3. Для проведения дегустации каждый конкурсный пунш готовится в объёме, достаточном для предоставления дегустационных порций членам жюри.
                    7.4. Участникам не требуется присутствовать при приготовлении конкурсных пуншей и проведеведении дегустации.
                    7.5. В рамках данного этапа члены жюри знают авторство и состав конкурсных пуншей.
                    7.6. Каждый член жюри оценивает каждый конкурсный пунш по трём критериям по шкале от 0 (нуля) до 10 (десяти) баллов:
                    - вкус — общее вкусовое качество и баланс пунша;
                    - оригинальность — оригинальность идеи, рецептуры и подхода к созданию пунша;
                    - выраженность и гармоничность вкусового профиля Jameson — степень, в которой рецептура пунша раскрывает, дополняет и подчёркивает характерные вкусовые особенности используемого Jameson.
                    7.7. Максимальное количество баллов, которое может выставить один член жюри за один конкурсный пунш, составляет 30 (тридцать) баллов.
                    7.8. Итоговая оценка каждого конкурсный пунша формируется путём суммирования баллов, выставленных тремя членами жюри. Максимально возможный итоговый балл составляет 90 (девяносто) баллов.
                    7.9. По результатам оценки жюри в финал Конкурса проходят 5 (пять) участников, набравших наибольшее количество баллов.
                    7.10. В случае равенства итоговых баллов у двух или более участников преимущество получает участник, набравший большее количество баллов по критерию «Выраженность и гармоничность вкусового профиля Jameson».
                    7.11. В случае равенства баллов и по указанному критерию преимущество получает участник, набравший большее количество баллов на первом этапе — онлайн-отборе профессиональных участников.
                    7.12. Участники, вошедшие в TOP 5, становятся финалистами Конкурса и получают право принять участие в финальном этапе.
                    7.13. Информация о финалистах TOP 5 публикуется на официальном Instagram-аккаунте JamesonKZ.

                    <b>8. Финальный этап Конкурса</b>

                    8.1. Финальный этап Конкурса проводится 24 сентября 2026 года в городе Алматы.
                    8.2. В финальном этапе принимают участие 5 (пять) финалистов, прошедших отбор в соответствии с разделом 7 настоящего Регламента.
                    8.3. Каждый финалист обязан лично приготовить свой конкурсный пунш в соответствии с рецептом, указанным в поданной заявке.
                    8.4. Изменение рецептуры конкурсного пунша, заявленной участником при подаче заявки, на финальном этапе не допускается.
                    8.5. Каждый финалист должен приготовить конкурсный пунш в объёме, рассчитанном на 45 (сорок пять) человек.
                    8.6. Для приготовления конкурсных пуншей Организатор предоставляет финалистам необходимые ингредиенты, оборудование, посуду для приготовления и подачи, а также иные материалы, необходимые для проведения финального этапа.
                    8.7. Финалист обязан заранее предоставить Организатору полный перечень ингредиентов, необходимых для приготовления конкурсного пунша. Закупка указанных ингредиентов осуществляется Организатором.
                    8.8. Финалисты самостоятельно осуществляют приготовление конкурсных пуншей с использованием предоставленных Организатором ингредиентов и оборудования.
                    8.9. После завершения приготовления конкурсного пунша финалист покидает зону проведения оценки и не принимает участия в процессе дегустации и оценивания.
                    8.10. Финальная дегустация проводится в формате вечеринки с конкурсными пуншами. Презентация конкурсных работ финалистами не предусмотрена.
                    8.11. Для подачи конкурсных пуншей Организатор предоставляет финалистам punch bowls или иные соответствующие ёмкости.
                    8.12. Каждый конкурсный пунш должен быть представлен для дегустации гостями мероприятия и членами жюри в полном соответствии с рецептом, заявленным участником.
                    8.13. Порядок проведения финального мероприятия, включая время приготовления конкурсных пуншей, последовательность выступления финалистов и организационные детали, определяется Организатором и доводится до сведения финалистов заранее.

                    <b>9. Жюри и оценивание финального этапа</b>

                    9.1. Для оценки конкурсных пуншей на финальном этапе формируется жюри в составе:
                    - 3 (трёх) профессиональных членов жюри;
                    - победителя конкурса среди потребителей;
                    - 40 (сорока) приглашённых судей, отобранных Организатором из числа гостей заведений общественного питания и представителей барной индустрии.
                    9.2. Каждый член основного жюри и каждый приглашённый судья оценивает каждый конкурсный пунш по шкале от 0 (нуля) до 10 (десяти) баллов.
                    9.3. Основным критерием оценки конкурсных пуншей на финальном этапе является вкус.
                    9.4. Каждый судья выставляет каждому конкурсному пуншу одну итоговую оценку от 0 до 10 баллов на основании своих вкусовых впечатлений.
                    9.5. Для участия в голосовании приглашённые судьи получают специальные оценочные карточки, предоставленные Организатором.
                    9.6. После завершения дегустации Организатор осуществляет подсчёт всех выставленных оценок.
                    9.7. Победителем Конкурса становится финалист, набравший наибольшее суммарное количество баллов, полученных от всех членов жюри и приглашённых судей.
                    9.8. Оценки всех членов жюри и приглашённых судей имеют равный вес при определении победителя.
                    9.9. В случае равенства суммарного количества баллов у двух или более финалистов победитель определяется коллегиальным решением членов основного жюри.
                    
                    <b>9.10. Специальный приз «Выбор жюри»</b>
                    9.10.1. Помимо основного победителя Конкурса определяется обладатель специального приза «Выбор жюри».
                    9.10.2. Обладателем специального приза «Выбор жюри» становится финалист, набравший наибольшее суммарное количество баллов по оценкам трёх профессиональных членов основного жюри и победителя конкурса среди потребителей, без учёта оценок 40 приглашённых судей.
                    9.10.3. Специальный приз «Выбор жюри» предоставляется брендом Jameson. Конкретный вид и содержание специального приза определяются Организатором и могут быть объявлены дополнительно.

                    <b>10. Призы</b>

                    10.1. Победитель Конкурса получает главный приз — поездку в Афины (Греция).
                    10.2. Организатор обеспечивает победителю оплату расходов, связанных с поездкой, включая авиаперелет, оформление визы и проживание на период поездки.
                    10.3. Денежная компенсация, денежный эквивалент стоимости поездки, замена главного приза на иной приз либо иная компенсация не предоставляются в случае, если победитель не может воспользоваться главным призом по причинам, не зависящим от Организатора, включая, но не ограничиваясь, отказом в выдаче визы, невозможностью получения необходимых для поездки документов, наличием ограничений на выезд или въезд, отсутствием у победителя действующего паспорта, несоответствием срока действия паспорта требованиям для совершения поездки, а также иными обстоятельствами, препятствующими совершению поездки и не зависящими от Организатора. 
                    10.4. Победителю предоставляется возможность по собственному желанию представить и разливать свой конкурсный пунш на стенде Jameson в рамках поездки. Использование данной возможности не является обязательным условием получения главного приза.
                    10.5. Конкретные даты поездки и иные организационные детали определяются Организатором и доводятся до сведения победителя дополнительно.
                    10.6. Обладатель специального приза «Выбор жюри» получает специальный приз от бренда Jameson в соответствии с пунктом 9.10 настоящего Регламента.
                    10.7. Конкретный вид, содержание и порядок вручения специальных призов определяются Организатором и могут быть объявлены дополнительно.

                    <b>11. Права на материалы и их использование</b>

                    11.1. Участник, принимая участие в Конкурсе, подтверждает своё согласие на фото-, видео- и аудиозапись, а также на создание иных материалов с его участием в рамках Конкурса и связанных с ним мероприятий.
                    11.2. Участник предоставляет Pernod Ricard Kazakhstan право использовать созданные в рамках Конкурса фото-, видео-, аудио- и иные материалы с его участием, а также предоставленные им материалы, включая фотографии, текстовые материалы, рецепты, концепции и иные результаты творческой деятельности, связанные с участием в Конкурсе.
                    11.3. Предоставляемое право включает право воспроизводить, распространять, публично показывать, публично исполнять, доводить до всеобщего сведения, размещать в сети Интернет, использовать в социальных сетях, рекламных и информационных материалах, а также иным образом использовать указанные материалы в любых формах и на любых носителях, известных на дату предоставления права или которые будут созданы в будущем.
                    11.4. Права, предусмотренные настоящим разделом, предоставляются Pernod Ricard Kazakhstan на безвозмездной основе, без ограничения территории и срока использования, если иное прямо не предусмотрено отдельным письменным соглашением между участником и Pernod Ricard Kazakhstan.
                    11.5. Участник предоставляет Pernod Ricard Kazakhstan право редактировать, сокращать, перерабатывать, адаптировать, компоновать с другими материалами и использовать отдельные фрагменты предоставленных материалов без дополнительного согласования с участником, при условии сохранения их общего содержания и отсутствия искажения смысла.
                    11.6. Участник предоставляет Pernod Ricard Kazakhstan право использовать его имя, фамилию, псевдоним, изображение, голос, сведения о месте работы, название конкурсной работы и иные сведения, предоставленные участником в рамках Конкурса, в связи с использованием материалов, указанных в настоящем разделе.
                    11.7. Участник подтверждает, что предоставляемые им материалы созданы им самостоятельно либо он обладает всеми необходимыми правами и разрешениями для их предоставления и использования Pernod Ricard Kazakhstan в соответствии с настоящим Регламентом.
                    11.8. В случае предъявления третьими лицами претензий, связанных с нарушением их прав вследствие предоставления участником материалов в рамках Конкурса, участник обязуется самостоятельно урегулировать такие претензии и несёт ответственность за наличие у него необходимых прав на предоставленные материалы.
                    11.9. Участник настоящим предоставляет Pernod Ricard Kazakhstan право использовать рецепт конкурсного пунша, включая его название, состав, рецептуру, технологию приготовления и концепцию, в целях создания, публикации и распространения информационных, рекламных, маркетинговых и иных материалов, связанных с брендом Jameson и деятельностью Pernod Ricard Kazakhstan.
                    11.10. Участник подтверждает, что не будет предъявлять к Pernod Ricard Kazakhstan требований о выплате дополнительного вознаграждения в связи с использованием материалов, рецепта, имени, изображения или иных результатов участия в Конкурсе в пределах прав, предоставленных настоящим Регламентом.

                    <b>12. Организационные и заключительные положения</b>

                    12.1. Организатор вправе вносить изменения и дополнения в условия и порядок проведения Конкурса, включая изменение сроков проведения отдельных этапов, при условии информирования участников посредством официальных каналов коммуникации Конкурса.
                    12.2. Организатор вправе отказать в допуске к участию в Конкурсе, исключить участника из Конкурса или аннулировать результаты его участия в случае выявления несоответствия участника или его конкурсной работы требованиям настоящего Регламента, предоставления недостоверной информации либо нарушения установленных условий Конкурса.
                    12.3. Решения Организатора по вопросам, связанным с организацией и проведением Конкурса, а также по вопросам, не урегулированным настоящим Регламентом, являются окончательными.
                    12.4. Участие в Конкурсе означает полное и безоговорочное согласие участника с настоящим Регламентом.
                    12.5. Все расходы, связанные с подготовкой конкурсной работы и участием в Конкурсе, если иное прямо не предусмотрено настоящим Регламентом, участник несёт самостоятельно.
                    12.6. Организатор не несёт ответственности за невозможность участия в Конкурсе по причинам, не зависящим от Организатора, включая технические сбои, перебои в работе интернет-сервисов, социальных сетей и иных информационных систем.
                    12.7. В случае возникновения обстоятельств, препятствующих проведению Конкурса в установленном порядке, Организатор вправе изменить формат, сроки или порядок проведения Конкурса либо отменить его, уведомив об этом участников посредством официальных каналов коммуникации.
                    12.8. Во всех случаях, не урегулированных настоящим Регламентом, окончательное решение принимает Организатор.`,
},
                rules: {
                    title: "Полные правила активности",
                    content: `<b>1. Общие положения</b>

1.1. Активация не является лотереей, азартной игрой или иным мероприятием,
регулируемым Законом Республики Казахстан от 9 апреля 2016 года № 495-V «О лотереях
и лотерейной деятельности».
1.2. Участие в активации является добровольным и означает полное и безусловное
согласие участника с настоящими Правилами.
1.3. Пул предоставляемых возможностей формируется за счёт средств Организатора.

<b>2. Условия участия</b>

2.1. К участию допускаются только физические лица, достигшие 21 года на момент
участия, постоянно проживающие на территории Республики Казахстан, не являющиеся
сотрудниками Организатора, а также сотрудниками подрядных организаций, причастных к
проведению активации, и их близкими родственниками.
2.2. Принимая участие в активации, участник подтверждает, что настоящие Правила им
прочитаны и понятны, и что участник безоговорочно и в полном объеме соглашается с
ними, без каких-либо ограничений и исключений.
2.3. Для участия участнику необходимо:
2.3.1. подписаться на официальный аккаунт @jamesonkz в социальной сети Instagram;
2.3.2. сделать репост конкурсной публикации в сторис;
2.3.3. пройти регистрацию на официальном сайте Организатора.
2.4. Один пользователь Instagram может участвовать только единожды, с одного личного
аккаунта.

<b>3. Порядок определения победителей</b>

3.1. По завершении периода проведения Активации Организатор в случайном порядке
определяет победителей среди участников, выполнивших все условия.
3.2. Победители будут уведомлены Организатором через контактные данные, указанные при регистрации.

<b>4. Ограничения и ответственность</b>

4.1. Организатор не несёт ответственности за невозможность участия в Активации по причинам,
не зависящим от Организатора.
4.2. Организатор оставляет за собой право отказать в учёте участия или аннулировать
результат при выявлении фактов нарушения участником настоящих Правил.

<b>5. Заключительные положения</b>

5.1. Факт участия в активации означает ознакомление и полное согласие участника с
настоящими Правилами.`
                }
            }
        };
    },
    computed: {
        ...mapGetters({
            userAge: "getUserAge"
        }),
        currentRoleTitle() {
            return this.selectedRole === "bartender" ? "Я БАРМЕН" : "Я ЛЮБИТЕЛЬ ПУНШЕЙ";
        },
        currentCampaignId() {
            return this.selectedRole === "bartender"
                ? this.campaignIds.bartender
                : this.campaignIds.enthusiast;
        },
        normalQuestions() {
            if (!this.formSchema?.questions) return [];
            return this.formSchema.questions.filter(q => !this.isCheckbox(q) && q.type !== 3);
        },
        filteredQuestions() {
            if (!this.formSchema?.questions) return [];
            return this.formSchema.questions.filter(q => this.isCheckbox(q));
        },
        bartenderSections() {
            if (!this.formSchema?.questions) {
                return {
                    contacts: [],
                    punchTop: [],
                    punchBottom: [],
                    recipeTop: [],
                    recipeBottom: [],
                    instagram: [],
                    consents: [],
                    extra: []
                };
            }
            const questions = this.formSchema.questions;
            const isPlaced = {};

            const findQ = (pred) => {
                const found = questions.find(q => !isPlaced[q.id] && pred(q));
                if (found) isPlaced[found.id] = true;
                return found;
            };

            // Consents first (unique by ID)
            const rawConsents = questions.filter(q => this.isCheckbox(q));
            const seenConsentIds = new Set();
            const consents = [];
            rawConsents.forEach(q => {
                if (!seenConsentIds.has(q.id)) {
                    seenConsentIds.add(q.id);
                    consents.push(q);
                    isPlaced[q.id] = true;
                }
            });

            // Exclude birth question
            const birthQ = questions.find(q => q.type === 3);
            if (birthQ) isPlaced[birthQ.id] = true;

            // Section 1: Contacts
            const firstName = findQ(q => q.type === 1 || (q.name && q.name.toLowerCase().includes("имя") && !q.name.toLowerCase().includes("пунш") && !q.name.toLowerCase().includes("фамил")));
            const lastName = findQ(q => q.type === 2 || (q.name && q.name.toLowerCase().includes("фамил")));
            const email = findQ(q => q.type === 7 || (q.name && (q.name.toLowerCase().includes("email") || q.name.toLowerCase().includes("почт"))));
            const phone = findQ(q => q.type === 4 || (q.name && (q.name.toLowerCase().includes("телефон") || q.name.toLowerCase().includes("номер"))));
            const barName = findQ(q => q.name && (q.name.toLowerCase().includes("бар") || q.name.toLowerCase().includes("заведен") || q.name.toLowerCase().includes("место работы")));
            const position = findQ(q => (q.name && (q.name.toLowerCase().includes("должност") || q.name.toLowerCase().includes("позици") || q.name.toLowerCase().includes("професси") || q.name.toLowerCase().includes("роль") || q.name.toLowerCase().includes("кем"))));

            const contacts = [firstName, lastName, email, phone, barName, position].filter(Boolean);

            // Section 2: About Punch
            const punchName = findQ(q => q.name && (q.name.toLowerCase().includes("назван") || q.name.toLowerCase().includes("имя пунша")));
            const punchConcept = findQ(q => q.name && (q.name.toLowerCase().includes("концепц") || q.name.toLowerCase().includes("вдохновен") || q.name.toLowerCase().includes("истори")));
            const punchRelease = findQ(q => q.type === 9 || (q.name && (q.name.toLowerCase().includes("релиз") || q.name.toLowerCase().includes("jameson") || q.name.toLowerCase().includes("продукт"))));
            const punchVolume = findQ(q => q.name && (q.name.toLowerCase().includes("объем") || q.name.toLowerCase().includes("объём") || q.name.toLowerCase().includes("мл") || q.name.toLowerCase().includes("литр")));

            const punchTop = [punchName, punchConcept].filter(Boolean);
            const punchBottom = [punchRelease, punchVolume].filter(Boolean);

            // Section 3: Full Recipe
            const recipeIngredients = findQ(q => q.name && (q.name.toLowerCase().includes("ингредиент") || q.name.toLowerCase().includes("состав") || q.name.toLowerCase().includes("пропорци")));
            const recipeTechnique = findQ(q => q.name && (q.name.toLowerCase().includes("техник") || q.name.toLowerCase().includes("приготовлен") || q.name.toLowerCase().includes("способ")));
            const recipePreparations = findQ(q => q.name && (q.name.toLowerCase().includes("заготов") || q.name.toLowerCase().includes("домашн") || q.name.toLowerCase().includes("сироп") || q.name.toLowerCase().includes("кордиал")));

            const recipeTop = [recipeIngredients, recipeTechnique].filter(Boolean);
            const recipeBottom = [recipePreparations].filter(Boolean);

            // Section 4: Instagram
            const instagramPost = findQ(q => q.name && (q.name.toLowerCase().includes("пост") || (q.name.toLowerCase().includes("ссылк") && q.name.toLowerCase().includes("instagram")) || q.name.toLowerCase().includes("url")));
            const instagramHandle = findQ(q => q.name && (q.name.toLowerCase().includes("аккаунт") || q.name.toLowerCase().includes("handle") || q.name.toLowerCase().includes("профиль") || q.name.toLowerCase().includes("instagram")));

            const instagram = [instagramPost, instagramHandle].filter(Boolean);

            // Any leftover normal questions
            const extra = questions.filter(q => !isPlaced[q.id] && q.type !== 3);

            return {
                contacts,
                punchTop,
                punchBottom,
                recipeTop,
                recipeBottom,
                instagram,
                consents,
                extra
            };
        },
        enthusiastSections() {
            if (!this.formSchema?.questions) {
                return {
                    userData: [],
                    userRecipe: [],
                    consents: [],
                    extra: []
                };
            }

            const questions = this.formSchema.questions;
            const isPlaced = {};

            const findQ = (pred) => {
                const found = questions.find(q => !isPlaced[q.id] && pred(q));
                if (found) isPlaced[found.id] = true;
                return found;
            };

            // Consents (unique by ID)
            const rawConsents = questions.filter(q => this.isCheckbox(q));
            const seenConsentIds = new Set();
            const consents = [];
            rawConsents.forEach(q => {
                if (!seenConsentIds.has(q.id)) {
                    seenConsentIds.add(q.id);
                    consents.push(q);
                    isPlaced[q.id] = true;
                }
            });

            // Exclude birth question (AgeGate)
            const birthQ = questions.find(q => q.type === 3);
            if (birthQ) isPlaced[birthQ.id] = true;

            // Секция 1: ВАШИ ДАННЫЕ (Имя/Фамилия, Email, Телефон если есть)
            const firstName = findQ(q => q.type === 1 || (q.name && (q.name.toLowerCase().includes("имя") || q.name.toLowerCase().includes("фио")) && !q.name.toLowerCase().includes("пунш")));
            const lastName = findQ(q => q.type === 2 || (q.name && q.name.toLowerCase().includes("фамил")));
            const email = findQ(q => q.type === 7 || (q.name && (q.name.toLowerCase().includes("email") || q.name.toLowerCase().includes("почт"))));
            const phone = findQ(q => q.type === 4 || (q.name && (q.name.toLowerCase().includes("телефон") || q.name.toLowerCase().includes("номер"))));

            const userData = [firstName, lastName, email, phone].filter(Boolean);

            // Секция 2: ВАШ РЕЦЕПТ (Название пунша, Описание и ингредиенты)
            const punchName = findQ(q => q.name && (q.name.toLowerCase().includes("назван") || q.name.toLowerCase().includes("имя пунша") || (q.name.toLowerCase().includes("пунш") && !q.name.toLowerCase().includes("рецепт"))));
            const punchRecipe = findQ(q => q.type === 8 || (q.name && (q.name.toLowerCase().includes("описан") || q.name.toLowerCase().includes("ингредиент") || q.name.toLowerCase().includes("рецепт") || q.name.toLowerCase().includes("состав"))));

            const userRecipe = [punchName, punchRecipe].filter(Boolean);

            // Любые остальные нераспределенные поля
            const extra = questions.filter(q => !isPlaced[q.id] && q.type !== 3);

            return {
                userData,
                userRecipe,
                consents,
                extra
            };
        },
        currentPopupData() {
            return this.popupTexts[this.currentPopup] || this.popupTexts.policy;
        },
        formattedTimer() {
            const pad = n => (n < 10 ? "0" + n : "" + n);
            return {
                days: pad(this.timerDays),
                hours: pad(this.timerHours),
                minutes: pad(this.timerMinutes),
                seconds: pad(this.timerSeconds)
            };
        }
    },
    watch: {
        userAge: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.showAgeConfirm = false;
                    if (this.formSchema?.questions) {
                        const birthQuestion = this.formSchema.questions.find(q => q.type === 3);
                        if (birthQuestion) {
                            this.$set(this.formData, birthQuestion.id, val);
                        }
                    }
                }
            }
        },
        formData: {
            deep: true,
            handler() {
                if (!this.formSchema?.questions) return;
                for (let question of this.formSchema.questions) {
                    if (this.touched[question.id]) {
                        this.validateField(question);
                    }
                }
            }
        }
    },
    created() {
        if (this.userAge) {
            this.showAgeConfirm = false;
        }
    },
    mounted() {
        window.addEventListener("resize", this.handleResize);
        initLanding({
            brand: 'jameson',
            activation: 'jameson_punch_competition_2026',
            landing_id: 'jameson-punch-2026',
            landing_type: 'competition'
        });
        this.updateCountdown();
        this.countdownInterval = setInterval(this.updateCountdown, 1000);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.handleResize);
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        if (this.smsTimerInterval) {
            clearInterval(this.smsTimerInterval);
        }
    },
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth < 1264;
        },
        onAgeConfirmed() {
            this.showAgeConfirm = false;
        },
        handleBackdropClick() {
            if (this.selectedRole) {
                this.showRoleModal = false;
            }
        },
        chooseRole(role) {
            this.selectedRole = role;
            this.showRoleModal = false;
            this.showBartenderForm = false;
            this.closeOtpPopup();
            this.formData = {};
            this.errors = {};
            this.touched = {};
            this.getFormFields(this.currentCampaignId);
            window.scrollTo({ top: 0, behavior: "smooth" });
        },
        toggleFaq(index) {
            this.$set(this.bartenderFaqs[index], "isOpen", !this.bartenderFaqs[index].isOpen);
        },
        handleOpenBartenderForm() {
            this.showBartenderForm = true;
            this.$nextTick(() => {
                const formEl = this.$refs.bartenderNoticeSection || document.getElementById("bartender-notice");
                if (formEl) {
                    formEl.scrollIntoView({ behavior: "smooth", block: "start" });
                }
            });
        },
        updateCountdown() {
            const now = new Date().getTime();
            const distance = this.targetDeadline - now;

            if (distance > 0) {
                this.timerDays = Math.floor(distance / (1000 * 60 * 60 * 24));
                this.timerHours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                this.timerMinutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                this.timerSeconds = Math.floor((distance % (1000 * 60)) / 1000);
            } else {
                this.timerDays = 0;
                this.timerHours = 0;
                this.timerMinutes = 0;
                this.timerSeconds = 0;
            }
        },

        isCheckbox(question) {
            if (!question) return false;
            if ([11, 12, 13, 14].includes(question.type)) return true;
            const name = (question.name || "").toLowerCase();
            return (
                name.includes("соглас") ||
                name.includes("подтвержд") ||
                name.includes("ознакомл") ||
                name.includes("персональн") ||
                name.includes("обработк") ||
                name.includes("21 год") ||
                name.includes("21 лет") ||
                name.includes("оригинальн") ||
                name.includes("опубликован") ||
                name.includes("новост") ||
                name.includes("акциях") ||
                name.includes("правил") ||
                name.includes("политик") ||
                name.includes("регламент")
            );
        },

        getCheckboxInfo(question, index = 0) {
            if (!question) {
                return {
                    isSpecial: false,
                    text: ""
                };
            }
            const name = (question.name || "").trim();
            const lower = name.toLowerCase();

            // 1. Personal Data Processing Consent
            if (lower.includes("персональн") || lower.includes("обработк") || lower.includes("оферт")) {
                return {
                    isSpecial: true,
                    type: "personal_data",
                    prefix: "Я даю согласие на ",
                    linkText: "сбор и обработку персональных данных",
                    popup: "offer"
                };
            }

            // 2. Regulations Consent
            if (lower.includes("регламент")) {
                return {
                    isSpecial: true,
                    type: "reglament",
                    prefix: "Я ознакомлен и согласен с ",
                    linkText: "регламентом конкурса",
                    popup: "reglament"
                };
            }

            // 3. Competition Rules Consent
            if (lower.includes("правил") || lower.includes("услови") || lower.includes("положен")) {
                return {
                    isSpecial: true,
                    type: "rules",
                    prefix: "Я ознакомлен и согласен с ",
                    linkText: "правилами конкурса",
                    popup: "rules"
                };
            }

            // 3. Marketing / Newsletter / Ads
            if (lower.includes("рассылк") || lower.includes("новост") || lower.includes("акци") || lower.includes("маркетинг") || lower.includes("реклам")) {
                return {
                    isSpecial: false,
                    text: name || "Я согласен получать информацию о специальных предложениях и мероприятиях"
                };
            }

            // 4. Age verification
            if (lower.includes("21") || lower.includes("возраст") || lower.includes("совершеннолет")) {
                return {
                    isSpecial: false,
                    text: name || "Подтверждаю, что мне исполнился 21 год"
                };
            }

            // Fallback for unlabeled checkboxes by order
            if (!name) {
                if (index === 0) {
                    return {
                        isSpecial: true,
                        type: "personal_data",
                        prefix: "Я даю согласие на ",
                        linkText: "сбор и обработку персональных данных",
                        popup: "offer"
                    };
                }
                if (index === 1) {
                    return {
                        isSpecial: true,
                        type: "rules",
                        prefix: "Я ознакомлен и согласен с ",
                        linkText: "правилами конкурса",
                        popup: "rules"
                    };
                }
                return {
                    isSpecial: false,
                    text: "Я согласен получать информацию о специальных предложениях и мероприятиях"
                };
            }

            return {
                isSpecial: false,
                text: name
            };
        },

        isCheckboxRequired(question) {
            if (!question) return false;
            if (question.type === 12) return false;
            const name = (question.name || "").toLowerCase();
            if (name.includes("рассылк") || name.includes("новост") || name.includes("маркетинг") || name.includes("реклам")) {
                return false;
            }
            return true;
        },

        isSelect(question) {
            if (!question) return false;
            if (this.isCheckbox(question)) return false;
            if (Array.isArray(question.answers) && question.answers.length > 0) return true;
            return [5, 6, 9].includes(question.type);
        },

        isTextarea(question) {
            if (!question) return false;
            if (this.isCheckbox(question) || this.isSelect(question)) return false;
            if (question.type === 8) return true;
            const name = (question.name || "").toLowerCase();
            return (
                name.includes("рецепт") ||
                name.includes("концепц") ||
                name.includes("вдохновен") ||
                name.includes("ингредиент") ||
                name.includes("техник") ||
                name.includes("приготовлен") ||
                name.includes("заготов") ||
                name.includes("описан") ||
                name.includes("пропорци")
            );
        },

        isUrlField(question) {
            if (!question) return false;
            const name = (question.name || "").toLowerCase();
            return question.type === 10 && (name.includes("ссылк") || name.includes("url") || name.includes("пост") || name.includes("http"));
        },

        isFullWidth(question) {
            if (!question) return false;
            if (this.isTextarea(question)) return true;
            const name = (question.name || "").toLowerCase();
            return (
                name.includes("название") ||
                name.includes("ссылка") ||
                name.includes("ссылку") ||
                name.includes("url") ||
                name.includes("пост")
            );
        },

        isLargeTextarea(question) {
            const name = (question.name || "").toLowerCase();
            return name.includes("ингредиент") || name.includes("техник") || name.includes("рецепт");
        },

        getPlaceholder(question) {
            if (!question) return "";
            const name = (question.name || "").toLowerCase();
            if (name.includes("должност") || name.includes("позици") || name.includes("професси") || name.includes("роль") || name.includes("кем")) {
                return "Выберите должность";
            }
            if (name.includes("релиз") || name.includes("jameson")) {
                return "Выберите релиз Jameson";
            }
            if (name.includes("город")) {
                return "Выберите город";
            }
            if (this.isSelect(question)) {
                return "Выберите из списка";
            }
            switch (question.type) {
                case 1:
                    return "Иван";
                case 2:
                    return "Иванов";
                case 4:
                    return "+7 (___) ___-__-__";
                case 5:
                    return "Выберите город";
                case 7:
                    return "example@mail.com";
                case 9:
                    return "Выберите релиз Jameson";
                case 10: {
                    if (name.includes("пост") || name.includes("ссылк") || name.includes("url")) {
                        return "https://www.instagram.com/p/...";
                    }
                    if (name.includes("аккаунт") || name.includes("instagram") || name.includes("инстаграм") || name.includes("логин")) {
                        return "@your_handle";
                    }
                    return question.name || "";
                }
                default:
                    return question.name || "";
            }
        },

        // --- DYNAMIC FORM FETCH & VALIDATION ---
        getFormFields(campaignId) {
            if (!campaignId) return;
            this.isLoadingForm = true;
            this.formError = false;
            this.$http.get("/api/survey", {
                params: {
                    campaignId: campaignId
                }
            })
            .then(res => {
                this.formSchema = res.data;
                this.formData = {};
                this.errors = {};
                this.touched = {};
                if (res.data && res.data.questions) {
                    res.data.questions.forEach(q => {
                        if (q.type === 3) {
                            this.$set(this.formData, q.id, this.userAge || "");
                        } else if (this.isCheckbox(q)) {
                            this.$set(this.formData, q.id, false);
                        } else {
                            this.$set(this.formData, q.id, "");
                        }
                    });
                }
            })
            .catch(err => {
                this.formError = true;
                console.error("Ошибка загрузки формы", err);
            })
            .finally(() => {
                this.isLoadingForm = false;
            });
        },

        validateField(question) {
            if (!question) return;
            const value = this.formData[question.id];
            let error = null;

            if (this.isCheckbox(question)) {
                if (this.isCheckboxRequired(question) && !value && this.touched[question.id]) {
                    error = "Необходимо подтвердить согласие";
                }
            } else {
                if (value === undefined || value === null || String(value).trim() === "") {
                    error = "Поле обязательно для заполнения";
                }
            }

            if (error === null && value && !this.isCheckbox(question) && !this.isSelect(question)) {
                if (this.isTextarea(question) && String(value).length > 800) {
                    error = "Превышен лимит (максимум 800 символов)";
                }
                if (question.type === 1 || question.type === 2) {
                    if (!/^[А-Яа-яA-Za-z\s-]+$/.test(value)) {
                        error = "Введите корректные символы";
                    }
                }
                if (question.type === 7) {
                    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
                        error = "Введите корректный email";
                    }
                }
                if (question.type === 4) {
                    if (!/^\+7\(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(value)) {
                        error = "Введите полный номер телефона";
                    }
                }
            }

            this.$set(this.errors, question.id, error);
            return !error;
        },

        markTouched(question) {
            if (!question) return;
            this.$set(this.touched, question.id, true);
            this.validateField(question);
        },

        onPhoneFocus(question) {
            const formId = this.activeTab === 'pro' ? 'punch_pro_registration' : 'punch_enthusiast_registration';
            trackFormStart(formId);
            if (!this.formData[question.id]) {
                this.$set(this.formData, question.id, "+7");
            }
        },

        onPhoneInput(question) {
            const val = this.formData[question.id] || "";
            if (!val.startsWith("+7")) {
                this.$set(this.formData, question.id, "+7" + val.replace(/\D/g, "").slice(1));
            }
            this.validateField(question);
        },

        async confirmRegistration() {
            const formId = this.activeTab === 'pro' ? 'punch_pro_registration' : 'punch_enthusiast_registration';
            trackFormSubmit(formId);
            this.otpError = "";
            if (!this.formSchema || !this.formSchema.questions) return;

            this.formSchema.questions.forEach(q => {
                this.markTouched(q);
            });

            let hasErrors = false;
            this.formSchema.questions.forEach(q => {
                const val = this.formData[q.id];
                const err = this.errors[q.id];

                let fieldName = 'field_' + q.id;
                switch (q.type) {
                    case 1: fieldName = 'name'; break;
                    case 2: fieldName = 'surname'; break;
                    case 3: fieldName = 'birth_date'; break;
                    case 4: fieldName = 'phone'; break;
                    case 5: fieldName = 'city'; break;
                    case 7: fieldName = 'email'; break;
                    case 9: fieldName = 'product_choice'; break;
                    case 10: fieldName = 'instagram'; break;
                    case 11: fieldName = 'consent_personal_data'; break;
                    case 12: fieldName = 'consent_rules'; break;
                }

                let isEmpty = false;
                if (q.type === 3) {
                    isEmpty = false;
                } else if (this.isCheckbox(q)) {
                    if (this.isCheckboxRequired(q)) {
                        isEmpty = val !== true;
                    }
                } else {
                    isEmpty = val === null || val === undefined || String(val).trim() === "";
                }

                if (err || isEmpty) {
                    hasErrors = true;
                    let code = 'validation_error';
                    if (isEmpty) {
                        code = this.isCheckbox(q) ? 'consent_required' : `${fieldName}_empty`;
                    } else if (q.type === 4) {
                        code = 'phone_invalid_format';
                    } else if (q.type === 7) {
                        code = 'email_invalid_format';
                    } else if (q.type === 1 || q.type === 2) {
                        code = `${fieldName}_invalid_format`;
                    }
                    trackFormError({
                        formId,
                        errorType: 'validation',
                        fieldName,
                        errorTextCode: code
                    });
                }
            });

            if (hasErrors) {
                this.snackbarMessage = "Пожалуйста, заполните все обязательные поля корректно";
                this.snackbar = true;
                return;
            }

            const phoneQuestion = this.formSchema.questions.find(q => q.type === 4);
            if (phoneQuestion) {
                try {
                    this.isSendSms = true;
                    this.submitting = true;
                    const isSent = await this.sendVerificationCode();
                    if (isSent && !this.otpErrorSms) {
                        this.otpError = "";
                        this.otpSuccess = false;
                        this.showOtpPopup = true;
                        this.startSmsTimer();
                    }
                } catch (e) {
                    this.snackbarMessage = "Ошибка при отправке кода подтверждения";
                    this.snackbar = true;
                } finally {
                    this.isSendSms = false;
                    this.submitting = false;
                }
            } else {
                await this.submitAnswersDirect();
            }
        },

        async sendVerificationCode() {
            const phoneQuestion = this.formSchema?.questions?.find(q => q.type === 4);
            if (!phoneQuestion) return false;

            const phoneAnswerRaw = this.formData[phoneQuestion.id];
            if (!phoneAnswerRaw) return false;
            const phoneAnswer = phoneAnswerRaw.replace(/[^\d+]/g, "");

            try {
                let recaptchaToken = "";
                if (this.$recaptchaLoaded && this.$recaptcha) {
                    try {
                        await this.$recaptchaLoaded();
                        recaptchaToken = await this.$recaptcha("send_sms");
                    } catch (rErr) {
                        console.warn("Recaptcha notice:", rErr);
                    }
                }

                const answerDto = {
                    campaignId: this.currentCampaignId,
                    surveyAnswer: {
                        surveyQuestionId: phoneQuestion.id,
                        answer: phoneAnswer
                    },
                    recaptchaToken: recaptchaToken
                };

                await this.$http.post("/api/Verification/SendCode", answerDto);
                this.otpErrorSms = false;
                return true;
            } catch (err) {
                console.error("Ошибка при отправке кода:", err);
                if (err?.response?.status === 409) {
                    this.otpErrorSms = true;
                    this.showOtpPopup = true;
                    this.otpSuccess = false;
                    return false;
                }
                if (err?.response?.status === 403) {
                    this.snackbarMessage = "Подозрение на бота. Пожалуйста, перезагрузите страницу.";
                    this.snackbar = true;
                    return false;
                }
                this.snackbarMessage = "Ошибка при отправке кода. Попробуйте снова.";
                this.snackbar = true;
                return false;
            }
        },

        async resendSms() {
            this.startSmsTimer();
            await this.sendVerificationCode();
        },

        formatAnswer(key, value) {
            const question = this.formSchema?.questions?.find(q => String(q.id) === String(key));
            if (!question) {
                let ans = String(value ?? "");
                if (/^\+7[\d\s()-]{10,}$/.test(ans)) {
                    ans = ans.replace(/[^\d+]/g, "");
                }
                return ans;
            }

            // If question has options (e.g. Yes/No binary options for checkbox type 14 or with options array)
            const options = (Array.isArray(question.options) && question.options.length > 0)
                ? question.options
                : (this.isCheckbox(question) && Array.isArray(question.answers) && question.answers.length > 0 ? question.answers : null);

            if (options && options.length > 0) {
                const isChecked = value === true || value === "true" || value === 1;
                if (isChecked) {
                    const yesOpt = options.find(opt => {
                        const name = (opt.name || "").toLowerCase().trim();
                        return name === "да" || name === "yes" || name.startsWith("да") || name.startsWith("yes");
                    }) || options[0];
                    return yesOpt ? String(yesOpt.id) : "true";
                } else {
                    const noOpt = options.find(opt => {
                        const name = (opt.name || "").toLowerCase().trim();
                        return name === "нет" || name === "no" || name.startsWith("нет") || name.startsWith("no");
                    }) || options[options.length - 1];
                    return noOpt ? String(noOpt.id) : "false";
                }
            }

            let answer = String(value ?? "");
            if (this.isTextarea(question) && answer.length > 800) {
                answer = answer.slice(0, 800);
            }
            if (/^\+7[\d\s()-]{10,}$/.test(answer) || question.type === 4) {
                answer = answer.replace(/[^\d+]/g, "");
            }
            return answer;
        },

        async submitAnswersWithCode(code) {
            this.otpVerifying = true;
            this.otpError = "";
            this.otpSuccess = false;
            try {
                const payload = {
                    surveyId: this.formSchema.id,
                    campaignId: this.currentCampaignId,
                    verificationCode: code,
                    answers: Object.entries(this.formData).map(([key, value]) => {
                        return {
                            surveyQuestionId: key,
                            answer: this.formatAnswer(key, value)
                        };
                    })
                };

                await this.$http.post("/api/AnswerSet", payload);
                this.otpSuccess = true;
                this.snackbarMessage = "Заявка успешно отправлена! Спасибо за участие.";
                this.snackbar = true;

                const formId = this.activeTab === 'pro' ? 'punch_pro_registration' : 'punch_enthusiast_registration';
                trackRegistrationSuccess({
                    formId,
                    city: this.formData[this.formSchema?.questions?.find(q => q.type === 5)?.id] || ''
                });
                trackVirtualPageView('/punch-competition/thank-you', 'Спасибо за участие в конкурсе');

                setTimeout(() => {
                    this.closeOtpPopup();
                    this.handleClearCurrentForm();
                }, 1800);
            } catch (error) {
                console.error(error);
                const msg = error?.response?.data?.message || "Код введён неверно";
                this.otpError = msg;
                const formId = this.activeTab === 'pro' ? 'punch_pro_registration' : 'punch_enthusiast_registration';
                trackFormError({
                    formId,
                    errorType: 'server',
                    fieldName: 'otp_code',
                    errorTextCode: 'otp_invalid_code'
                });
            } finally {
                this.otpVerifying = false;
            }
        },

        async submitAnswersDirect() {
            this.submitting = true;
            try {
                const payload = {
                    surveyId: this.formSchema.id,
                    campaignId: this.currentCampaignId,
                    answers: Object.entries(this.formData).map(([key, value]) => {
                        return {
                            surveyQuestionId: key,
                            answer: this.formatAnswer(key, value)
                        };
                    })
                };

                await this.$http.post("/api/AnswerSet", payload);
                this.snackbarMessage = "Заявка успешно отправлена! Спасибо за участие.";
                this.snackbar = true;
                this.handleClearCurrentForm();
            } catch (error) {
                console.error("Ошибка отправки заявки:", error);
                const msg = error?.response?.data?.message || "Ошибка отправки заявки. Попробуйте еще раз.";
                this.snackbarMessage = msg;
                this.snackbar = true;
            } finally {
                this.submitting = false;
            }
        },

        handleClearCurrentForm() {
            if (this.formSchema && this.formSchema.questions) {
                this.formSchema.questions.forEach(q => {
                    if (q.type === 3) {
                        this.$set(this.formData, q.id, this.userAge || "");
                    } else if (this.isCheckbox(q)) {
                        this.$set(this.formData, q.id, false);
                    } else {
                        this.$set(this.formData, q.id, "");
                    }
                });
            } else {
                this.formData = {};
            }
            this.errors = {};
            this.touched = {};
            this.snackbarMessage = "Форма очищена";
            this.snackbar = true;
        },

        startSmsTimer() {
            this.smsTimer = 60;
            if (this.smsTimerInterval) {
                clearInterval(this.smsTimerInterval);
            }
            this.smsTimerInterval = setInterval(() => {
                if (this.smsTimer > 0) {
                    this.smsTimer--;
                } else {
                    clearInterval(this.smsTimerInterval);
                    this.smsTimerInterval = null;
                }
            }, 1000);
        },

        closeOtpPopup() {
            this.showOtpPopup = false;
            this.otpSuccess = false;
            this.otpErrorSms = false;
            this.otpError = "";
            if (this.smsTimerInterval) {
                clearInterval(this.smsTimerInterval);
                this.smsTimerInterval = null;
            }
        },

        openPopup(type) {
            this.currentPopup = type;
            this.isPopupOpen = true;
        },

        closePopup() {
            this.isPopupOpen = false;
        }
    }
};
</script>

<style scoped>
/* ==========================================================================
   GLOBAL PAGE STYLES
   ========================================================================== */
.punch-competition-page {
    width: 100%;
    min-height: 100vh;
    overflow-x: hidden;
    color: #FEF8E1; /* Ivory / Cream text color */
    text-align: center;
    position: relative;
    background-color: #00483A;
}

.content-wrapper {
    width: 100%;
    text-align: center;
}

.landing-content-container {
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
}

/* ==========================================================================
   ROLE SELECTION POPUP MODAL (Clean dialog with backdrop blur)
   ========================================================================== */
.role-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.78);
    backdrop-filter: blur(8px);
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    overflow-y: auto;
}

.role-modal-card {
    position: relative;
    max-width: 580px;
    width: 100%;
    background-color: var(--color-primary-popup, #114b35);
    border-radius: 20px;
    padding: clamp(36px, 5vw, 52px) clamp(24px, 4vw, 44px);
    box-sizing: border-box;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(254, 248, 225, 0.1);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.role-card-close-icon {
    position: absolute;
    top: 16px;
    right: 16px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #FEF8E1;
    opacity: 0.75;
    transition: opacity 0.2s, transform 0.2s, background-color 0.2s;
    z-index: 10;
}

.role-card-close-icon:hover {
    opacity: 1;
    transform: scale(1.1);
    background-color: rgba(254, 248, 225, 0.1);
}

.role-card-content {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.role-modal-logo-wrapper {
    margin-bottom: 20px;
}

.role-modal-j-logo {
    width: 65px;
    height: auto;
    max-height: 90px;
    object-fit: contain;
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4));
}

/* Header: "ДОБРО ПОЖАЛОВАТЬ!" */
.role-modal-title {
    font-family: var(--font-family-heading, var(--font-family-bold, "OswaldBold", sans-serif));
    font-size: clamp(26px, 4.5vw, 38px);
    font-weight: 700;
    text-transform: uppercase;
    color: #FEF8E1;
    letter-spacing: 0.04em;
    line-height: 1.15;
    margin: 0 0 12px 0;
    text-align: center;
}

/* Subtitle: "Выберите вашу роль" */
.role-modal-subtitle {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(16px, 2.2vw, 19px);
    font-weight: 400;
    color: #FEF8E1;
    margin: 0 0 32px 0;
    opacity: 0.9;
    text-align: center;
}

/* Buttons container */
.role-modal-buttons {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 380px;
}

.role-choice-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 2px solid #FEF8E1;
    color: #FEF8E1;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    padding: 15px 28px;
    width: 100%;
    height: 52px;
    border-radius: 10px;
    cursor: pointer;
    letter-spacing: 0.05em;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    box-sizing: border-box;
}

.role-choice-btn:hover {
    background: #FEF8E1;
    color: #114b35;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(254, 248, 225, 0.3);
}

.role-choice-btn:active {
    transform: translateY(1px);
}

/* Modal Transition */
.modal-scale-enter-active,
.modal-scale-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-scale-enter,
.modal-scale-leave-to {
    opacity: 0;
}

.modal-scale-enter .role-modal-card,
.modal-scale-leave-to .role-modal-card {
    transform: scale(0.92);
}

/* ==========================================================================
   ROLE SWITCHER TOP BAR
   ========================================================================== */
.role-topbar {
    background-color: rgba(17, 75, 53, 0.95);
    border-bottom: 1px solid rgba(254, 248, 225, 0.2);
    padding: 10px 20px;
    position: sticky;
    top: 0;
    z-index: 100;
    backdrop-filter: blur(8px);
}

.role-topbar-inner {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.current-role-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-family, "jj2book");
    font-size: 14px;
    color: #FEF8E1;
}

.badge-dot {
    width: 8px;
    height: 8px;
    background-color: #FEF8E1;
    border-radius: 50%;
    display: inline-block;
    box-shadow: 0 0 6px #FEF8E1;
}

.badge-label {
    opacity: 0.8;
}

.badge-value {
    color: #FEF8E1;
    font-family: var(--font-family-bold, "jj2bold");
}

.switch-role-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px solid rgba(254, 248, 225, 0.35);
    color: #FEF8E1;
    font-family: var(--font-family, "jj2book");
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.switch-role-btn:hover {
    background: rgba(254, 248, 225, 0.15);
    border-color: #FEF8E1;
}

/* ==========================================================================
   BLOCK 1 & 2: HERO & COUNTDOWN TIMER SECTION (Custom Background ready)
   ========================================================================== */
.block-hero-timer-section {
    width: 100%;
    padding: 70px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    background-image: url(../assets/img/punch-first-block-bg.png);
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.block-hero {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
}

.hero-logo-wrapper {
    margin-bottom: 24px;
}

.jameson-white-logo {
    width: clamp(200px, 26vw, 300px);
    height: auto;
    object-fit: contain;
    /* filter: brightness(0) invert(1); */
}

.hero-main-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(34px, 6vw, 68px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    line-height: 1.1;
    color: #FEF8E1;
    margin: 0 0 16px 0;
    max-width: 660px;
    text-align: center;
}

.hero-subtitle {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(18px, 2.3vw, 24px);
    font-weight: 400;
    color: #FEF8E1;
    margin: 0;
    text-align: center;
    opacity: 0.95;
}

/* Timer and Info */
.block-timer-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
}

.gold-divider-line {
    width: 100%;
    max-width: 760px;
    height: 1.5px;
    background: linear-gradient(90deg, rgba(212, 175, 55, 0) 0%, #c69214 25%, #f1e4b2 50%, #c69214 75%, rgba(212, 175, 55, 0) 100%);
    margin: 40px auto 28px;
}

.timer-heading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(19px, 2.2vw, 23px);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #FEF8E1;
    margin-bottom: 22px;
}

.countdown-timer-grid {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: clamp(10px, 2vw, 20px);
    margin-bottom: 18px;
    flex-wrap: wrap;
}

.timer-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: clamp(75px, 12vw, 110px);
    height: clamp(85px, 13vw, 115px);
    padding: 12px 14px;
    border: 1.5px solid rgba(254, 248, 225, 0.45);
    border-radius: 12px;
    background: rgba(18, 76, 54, 0.35);
    backdrop-filter: blur(6px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
}

.timer-digit {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(34px, 5vw, 52px);
    font-weight: 800;
    line-height: 1;
    color: #FEF8E1;
}

.timer-unit {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(10px, 1.2vw, 12px);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: rgba(254, 248, 225, 0.85);
    margin-top: 8px;
}

.timer-deadline-date {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(15px, 1.8vw, 18px);
    font-weight: 600;
    color: #FEF8E1;
    margin-bottom: 0;
    opacity: 0.9;
}

/* ==========================================================================
   BLOCK: ЧТО ТАКОЕ JAMESON PUNCH COMPETITION (ОТДЕЛЬНЫЙ БЛОК)
   ========================================================================== */
.block-about-competition-section {
    width: 100%;
    background-color: var(--color-primary-popup, #114b35);
    padding: 70px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.about-competition-heading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(26px, 4vw, 40px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #FEF8E1;
    margin: 0 0 20px 0;
    text-align: center;
}

.about-competition-text {
    max-width: 820px;
    width: 100%;
    margin: 0 auto;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(16px, 1.8vw, 20px);
    line-height: 1.65;
    color: #FEF8E1;
    text-align: center;
    opacity: 0.95;
}

/* ==========================================================================
   BLOCK 3 (BARTENDER): ДЛЯ КОГО ЭТОТ КОНКУРС (Solid emerald #0d7c49, 60/40)
   ========================================================================== */
.block-bartender-target-section {
    width: 100%;
    background-color: var(--color-primary-light);
    padding: 70px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.target-container-inner {
    max-width: 1060px;
    width: 100%;
    margin: 0 auto;
}

.target-two-columns-layout {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: clamp(24px, 4vw, 48px);
    width: 100%;
}

.target-column-left {
    flex: 1 1 500px;
    max-width: 540px;
    text-align: left;
    display: flex;
    flex-direction: column;
}

.target-main-heading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(30px, 4.5vw, 46px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    line-height: 1.15;
    color: #FEF8E1;
    margin: 0 0 20px 0;
    text-align: left;
}

.target-lead-paragraph {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-weight: 700;
    font-size: clamp(16px, 1.8vw, 20px);
    line-height: 1.5;
    color: #FEF8E1;
    margin: 0 0 24px 0;
    text-align: left;
}

.bartender-requirements-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.bartender-requirements-list li {
    position: relative;
    padding-left: 26px;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(15px, 1.6vw, 18px);
    line-height: 1.5;
    color: #FEF8E1;
    text-align: left;
}

.bartender-requirements-list li::before {
    content: "•";
    position: absolute;
    left: 6px;
    top: -1px;
    color: #FEF8E1;
    font-size: 22px;
    line-height: 1;
}

.target-column-right {
    flex: 1 1 480px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 500px;
}

.target-bottles-image {
    width: 100%;
    max-width: 480px;
    height: auto;
    max-height: 480px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 16px 32px rgba(0, 0, 0, 0.4));
}

/* ==========================================================================
   BLOCK 4 (BARTENDER): ЭТАПЫ КОНКУРСА (#114b35 SOLID TIMELINE)
   ========================================================================== */
.block-stages-section {
    width: 100%;
    background-color: var(--color-primary-popup);
    padding: 80px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.stages-container-inner {
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.stages-timeline-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0;
    margin-top: 10px;
}

.timeline-stage-row {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 24px;
    position: relative;
}

.timeline-marker-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 44px;
}

.timeline-marker-circle {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: #1a8c5b;
    color: #FEF8E1;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 20px;
    font-weight: 800;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 12px rgba(26, 140, 91, 0.4);
    z-index: 2;
}

.timeline-marker-line {
    flex: 1;
    width: 2px;
    background: rgba(254, 248, 225, 0.3);
    margin: 6px 0;
    min-height: 40px;
}

.timeline-content-card {
    flex: 1;
    background: rgba(0, 0, 0, 0.22);
    border: 1px solid rgba(254, 248, 225, 0.15);
    border-radius: 12px;
    padding: 20px 24px;
    margin-bottom: 24px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    text-align: left;
    backdrop-filter: blur(6px);
    transition: transform 0.25s ease, border-color 0.25s ease;
}

.timeline-content-card:hover {
    border-color: rgba(254, 248, 225, 0.4);
    transform: translateX(4px);
}

.stage-card-left {
    flex: 0 0 240px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.stage-name-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(17px, 2vw, 20px);
    font-weight: 800;
    text-transform: uppercase;
    color: #FEF8E1;
    margin: 0 0 6px 0;
    letter-spacing: 0.02em;
}

.stage-dates-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 14px;
    color: rgba(254, 248, 225, 0.8);
    margin: 0;
}

.stage-card-divider {
    width: 1px;
    align-self: stretch;
    background-color: rgba(254, 248, 225, 0.2);
}

.stage-card-right {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.stage-desc-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(14px, 1.5vw, 16px);
    line-height: 1.45;
    color: #FEF8E1;
    margin: 0;
    opacity: 0.95;
}

.stage-step-image {
    flex-shrink: 0;
    width: 76px;
    height: 76px;
    max-width: 82px;
    max-height: 82px;
    object-fit: contain;
    display: block;
    filter: none;
    box-shadow: none;
    background: transparent;
    transition: transform 0.25s ease;
}

.timeline-content-card:hover .stage-step-image {
    transform: scale(1.08);
}

/* ==========================================================================
   BLOCK 5 (BARTENDER): СУДЕЙСКАЯ КОЛЛЕГИЯ
   ========================================================================== */
.block-judges-section {
    width: 100%;
    padding: 70px 20px 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-image: url(../assets/img/form-1-punch-bg.png);
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.judges-cards-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.judges-top-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    gap: clamp(16px, 3vw, 32px);
    width: 100%;
    flex-wrap: wrap;
}

.judges-bottom-row {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
}

.judge-card-item {
    flex: 1 1 260px;
    max-width: 310px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: 0 auto;
}

.judge-card-consumer-winner {
    max-width: 360px;
}

.judge-photo-wrapper {
    width: 100%;
    aspect-ratio: 491 / 434;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    background: rgba(255, 255, 255, 0.08);
    border: 1.5px solid rgba(254, 248, 225, 0.25);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    display: flex;
    justify-content: center;
    align-items: center;
    transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.judge-photo-wrapper:hover {
    transform: translateY(-4px);
    border-color: rgba(254, 248, 225, 0.7);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.35);
}

.judge-photo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.judge-photo-placeholder-wrapper {
    position: relative;
}

.judge-placeholder-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    background: rgba(255, 255, 255, 0.35);
    border-radius: 50%;
    width: 72px;
    height: 72px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.judge-name-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(16px, 1.8vw, 18px);
    font-weight: 700;
    text-transform: uppercase;
    color: #FEF8E1;
    margin: 16px 0 4px 0;
    line-height: 1.25;
    letter-spacing: 0.03em;
    text-align: center;
}

.judge-role-subtitle {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 13px;
    font-weight: 400;
    color: rgba(254, 248, 225, 0.8);
    margin: 0;
    line-height: 1.35;
    text-align: center;
}

/* ==========================================================================
   BLOCK 6 (BARTENDER): ТРЕБОВАНИЯ К ЗАЯВКЕ (#114b35 SOLID, 2 Columns)
   ========================================================================== */
.block-requirements-section {
    width: 100%;
    background-color: var(--color-primary-popup);
    padding: 80px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.requirements-container-inner {
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.requirements-columns-grid {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    gap: clamp(24px, 4vw, 48px);
    width: 100%;
    flex-wrap: wrap;
    margin-top: 10px;
}

.requirement-card-column {
    flex: 1 1 420px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: left;
}

.req-badge-header {
    background-color: #1a8c5b;
    border-radius: 8px;
    padding: 12px 20px;
    text-align: center;
    margin-bottom: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.req-badge-header span {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(16px, 1.8vw, 19px);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #FEF8E1;
    display: block;
}

.req-checklist {
    list-style: none;
    padding: 0;
    margin: 0 0 24px 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
}

.req-checklist li {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 14px;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(15px, 1.6vw, 17px);
    line-height: 1.45;
    color: #FEF8E1;
    text-align: left;
}

.req-check-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    margin-top: 2px;
    border-radius: 50%;
    background-color: rgba(26, 140, 91, 0.4);
    border: 1px solid rgba(254, 248, 225, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
}

.req-column-bottom-divider {
    width: 100%;
    height: 1.5px;
    background: rgba(254, 248, 225, 0.25);
    margin-top: auto;
}

/* ==========================================================================
   BLOCK 7 (BARTENDER): ПРИЗЫ БАРМЕНОВ (#0d7c49 SOLID)
   ========================================================================== */
.block-bartender-prizes-section {
    width: 100%;
    background-color: var(--color-primary-light);
    padding: 80px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.bartender-prizes-inner {
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.bartender-prizes-grid {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    gap: clamp(16px, 3vw, 32px);
    width: 100%;
    flex-wrap: wrap;
    margin-top: 10px;
}

.bartender-prize-card {
    flex: 1 1 260px;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: 0 auto;
}

.bartender-prize-photo-placeholder {
    width: 100%;
    aspect-ratio: 1 / 1;
    background: rgba(255, 255, 255, 0.12);
    border: 1.5px solid rgba(254, 248, 225, 0.35);
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    backdrop-filter: blur(8px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease, border-color 0.3s ease;
}

.bartender-prize-photo-placeholder:hover {
    transform: translateY(-4px);
    border-color: rgba(254, 248, 225, 0.7);
}

.bartender-prize-icon-inner {
    width: 100%;
    height: 100%;
    border-radius: 10px;
    background: rgba(254, 248, 225, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
}

.bartender-prize-heading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(17px, 1.9vw, 20px);
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #FEF8E1;
    margin: 18px 0 6px 0;
    line-height: 1.25;
    text-align: center;
}

.bartender-prize-desc {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(14px, 1.5vw, 16px);
    font-weight: 400;
    color: #FEF8E1;
    opacity: 0.9;
    margin: 0;
    line-height: 1.45;
    text-align: center;
}

/* ==========================================================================
   BLOCK 8 (BARTENDER): FAQ (#0c3826 SOLID)
   ========================================================================== */
.block-faq-section {
    width: 100%;
    background-color: var(--color-primary-popup);
    padding: 85px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.faq-container-inner {
    max-width: 1000px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.faq-accordion-list {
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-top: 14px;
}

.faq-accordion-item {
    width: 100%;
    display: flex;
    flex-direction: column;
}

.faq-item-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 22px 4px;
    cursor: pointer;
    user-select: none;
    transition: opacity 0.2s ease;
}

.faq-item-header:hover {
    opacity: 0.85;
}

.faq-question-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(16px, 1.8vw, 19px);
    font-weight: 800;
    text-transform: uppercase;
    color: #FEF8E1;
    letter-spacing: 0.03em;
    text-align: left;
    line-height: 1.35;
}

.faq-toggle-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 16px;
    transition: transform 0.25s ease;
}

.faq-item-body {
    padding: 0 4px 20px 4px;
    text-align: left;
}

.faq-answer-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(14px, 1.5vw, 16px);
    line-height: 1.6;
    color: #FEF8E1;
    opacity: 0.9;
    margin: 0;
    max-width: 900px;
}

.faq-item-divider {
    width: 100%;
    height: 1px;
    background: rgba(254, 248, 225, 0.2);
}

.faq-collapse-enter-active,
.faq-collapse-leave-active {
    transition: all 0.3s ease;
}

.faq-collapse-enter,
.faq-collapse-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* ==========================================================================
   BLOCK 9 (BARTENDER): CTA (#0d7c49 SOLID)
   ========================================================================== */
.block-cta-section {
    width: 100%;
    background-color: var(--color-primary-light);
    padding: 85px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.cta-container-inner {
    max-width: 750px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.cta-main-heading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(32px, 5.5vw, 52px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    color: #FEF8E1;
    margin: 0 0 16px 0;
    line-height: 1.1;
    text-align: center;
}

.cta-main-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(16px, 2vw, 20px);
    line-height: 1.55;
    color: #FEF8E1;
    margin: 0 0 32px 0;
    max-width: 680px;
    text-align: center;
}

.cta-ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 2px solid #FEF8E1;
    color: #FEF8E1;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 17px;
    font-weight: 700;
    text-transform: uppercase;
    padding: 16px 42px;
    min-width: 240px;
    height: 56px;
    border-radius: var(--radius-md, 10px);
    cursor: pointer;
    letter-spacing: 0.05em;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.cta-ghost-btn:hover {
    background: #FEF8E1;
    color: #0d7c49;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(254, 248, 225, 0.3);
}

.cta-ghost-btn:active {
    transform: translateY(1px);
}

/* Form transition */
.form-fade-enter-active,
.form-fade-leave-active {
    transition: opacity 0.5s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.form-fade-enter,
.form-fade-leave-to {
    opacity: 0;
    transform: translateY(30px);
}

.bartender-revealed-form-flow {
    width: 100%;
}

/* ==========================================================================
   BLOCK 11 (BARTENDER): ПРЕДУПРЕЖДЕНИЕ ПЕРЕД ФОРМОЙ (#0c3826 SOLID)
   ========================================================================== */
.block-form-notice-section {
    width: 100%;
    background-color: var(--color-primary-popup);
    padding: 75px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.notice-container-inner {
    max-width: 850px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.notice-main-heading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(28px, 4.5vw, 42px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    color: #FEF8E1;
    margin: 0 0 14px 0;
    line-height: 1.15;
    text-align: center;
}

.notice-subheading {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(20px, 2.5vw, 26px);
    font-weight: 800;
    color: #FEF8E1;
    margin: 0 0 16px 0;
    text-align: center;
}

.notice-description-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(16px, 1.8vw, 19px);
    line-height: 1.65;
    color: #FEF8E1;
    opacity: 0.95;
    margin: 0;
    max-width: 800px;
    text-align: center;
}

/* ==========================================================================
   BLOCK 12 (BARTENDER): САМА ФОРМА ЗАЯВКИ (#0d7c49 SOLID)
   ========================================================================== */
.block-bartender-full-form-section {
    width: 100%;
    background-color: var(--color-primary-light);
    padding: 85px 20px 100px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    text-align: left;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.form-loading-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 20px;
    width: 100%;
}

.form-error-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    width: 100%;
    text-align: center;
}

.form-error-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 18px;
    color: #FEF8E1;
    margin: 0;
}

.full-form-container-inner {
    max-width: 900px;
    width: 100%;
    margin: 0 auto;
}

.bartender-application-form {
    display: flex;
    flex-direction: column;
    gap: 40px;
    width: 100%;
    text-align: left;
}

.form-section-group {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    text-align: left;
}

.form-section-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(20px, 2.3vw, 25px);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #FEF8E1;
    margin: 0 0 4px 0;
    text-align: left;
    border-bottom: 1px solid rgba(254, 248, 225, 0.25);
    padding-bottom: 10px;
    line-height: 1.25;
}

.form-grid-two-cols,
.bartender-dynamic-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px 24px;
    width: 100%;
}

.enthusiast-fields-stack {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
}

.policy-link-inline {
    color: #FEF8E1 !important;
    text-decoration: underline;
    cursor: pointer;
    font-weight: 700;
}

.policy-link-inline:hover {
    opacity: 0.8;
}

/* Vuetify v-select styling */
::v-deep .custom-v-select {
    margin: 0;
    padding: 0;
    width: 100% !important;
}

::v-deep .custom-v-select .v-input__control > .v-input__slot {
    min-height: 50px !important;
    height: 50px !important;
    background: transparent !important;
    border: 1.5px solid rgba(254, 248, 225, 0.45) !important;
    border-radius: 8px !important;
    padding: 0 16px !important;
}

::v-deep .custom-v-select fieldset {
    display: none !important;
}

::v-deep .custom-v-select .v-select__selection--comma,
::v-deep .custom-v-select .select-selected-text,
::v-deep .custom-v-select input {
    color: #FEF8E1 !important;
    font-size: 15px !important;
    font-family: var(--font-family, "jj2book", sans-serif) !important;
}

::v-deep .custom-v-select input::placeholder {
    color: rgba(254, 248, 225, 0.45) !important;
    font-size: 14px !important;
    font-family: var(--font-family, "jj2book", sans-serif) !important;
}

::v-deep .custom-v-select .v-icon {
    color: #FEF8E1 !important;
}

.form-input-control {
    display: flex;
    flex-direction: column;
    text-align: left;
    width: 100%;
}

.full-width-control {
    grid-column: 1 / -1;
}

.input-top-label {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 13px;
    font-weight: 600;
    color: #FEF8E1;
    opacity: 0.8;
    margin-bottom: 6px;
    text-align: left;
}

.transparent-form-input {
    width: 100%;
    height: 50px;
    background: transparent;
    border: 1.5px solid rgba(254, 248, 225, 0.45);
    border-radius: 8px;
    padding: 12px 16px;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 15px;
    color: #FEF8E1;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.transparent-form-input::placeholder {
    color: rgba(254, 248, 225, 0.45);
    font-size: 14px;
}

.transparent-form-input:focus {
    border-color: #FEF8E1;
    box-shadow: 0 0 8px rgba(254, 248, 225, 0.3);
}

.transparent-textarea {
    height: auto;
    min-height: 110px;
    resize: vertical;
    line-height: 1.5;
}

.textarea-wrapper {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.char-counter {
    align-self: flex-end;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 12px;
    font-weight: 600;
    color: rgba(254, 248, 225, 0.65);
    margin-top: 4px;
    margin-bottom: 2px;
    text-align: right;
    transition: color 0.2s ease;
    user-select: none;
}

.char-counter-limit {
    color: #FEE79A !important;
    font-weight: 700;
}

.form-field-error {
    color: #ff8c8c;
    font-size: 12px;
    font-family: var(--font-family, "jj2book");
    margin-top: 4px;
    text-align: left;
}

/* Consent checkboxes */
.consent-checkboxes-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 4px;
}

/* Bartender form buttons row */
.bartender-form-buttons-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    margin-top: 10px;
    width: 100%;
}

.bartender-btn-submit {
    flex: 1 1 55%;
    height: 52px;
    background: #06402b;
    border: 1.5px solid rgba(254, 248, 225, 0.85);
    border-radius: 8px;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #FEF8E1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
}

.bartender-btn-submit:hover:not(:disabled) {
    background: #FEF8E1;
    color: #06402b;
    border-color: #FEF8E1;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(254, 248, 225, 0.35);
}

.bartender-btn-submit:active:not(:disabled) {
    transform: translateY(1px);
}

.bartender-btn-submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.bartender-btn-clear {
    flex: 1 1 45%;
    height: 52px;
    background: transparent;
    border: 1.5px solid rgba(254, 248, 225, 0.6);
    border-radius: 8px;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #FEF8E1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.bartender-btn-clear:hover {
    background: rgba(254, 248, 225, 0.15);
    border-color: #FEF8E1;
    transform: translateY(-2px);
}

.bartender-btn-clear:active {
    transform: translateY(1px);
}

/* ==========================================================================
   BLOCK 3 (ENTHUSIAST): О ЧЁМ ЭТО? (Solid lighter green #1a684b)
   ========================================================================== */
.block-enthusiast-about-section {
    width: 100%;
    background-color: var(--color-primary-light);
    padding: 70px 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.about-container-inner {
    max-width: 800px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.about-main-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(32px, 5vw, 52px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    color: #FEF8E1;
    margin: 0 0 18px 0;
    line-height: 1.1;
    text-align: center;
}

.about-main-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(17px, 2.2vw, 23px);
    line-height: 1.55;
    color: #FEF8E1;
    margin: 0 0 10px 0;
    max-width: 800px;
    text-align: center;
}

.enthusiast-steps-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 800px;
    margin-top: 28px;
    text-align: left;
}

.enthusiast-step-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;
    background: rgba(254, 248, 225, 0.08);
    border: 1.5px solid rgba(254, 248, 225, 0.25);
    border-radius: 14px;
    padding: 18px 24px;
    box-sizing: border-box;
    backdrop-filter: blur(8px);
    transition: transform 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
}

.enthusiast-step-item:hover {
    transform: translateY(-2px);
    border-color: rgba(254, 248, 225, 0.6);
    background: rgba(254, 248, 225, 0.12);
}

.enthusiast-step-number {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--color-primary-popup, #114b35);
    border: 2px solid #FEF8E1;
    color: #FEF8E1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 18px;
    font-weight: 700;
}

.enthusiast-step-content {
    flex: 1;
    display: flex;
    align-items: center;
}

.enthusiast-step-text {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(15px, 1.8vw, 18px);
    line-height: 1.5;
    color: #FEF8E1;
    margin: 0;
    text-align: left;
}

/* ==========================================================================
   BLOCK 4 (ENTHUSIAST): ПРИЗЫ ДЛЯ ЛЮБИТЕЛЕЙ
   ========================================================================== */
.block-enthusiast-prizes-section {
    width: 100%;
    padding: 70px 20px 80px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.section-title-left-wrap {
    width: 100%;
    text-align: left;
    margin-bottom: 28px;
}

.section-title-left {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.01em;
    color: #FEF8E1;
    margin: 0;
    text-align: left;
}

.prizes-cards-grid {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
    gap: clamp(16px, 3vw, 32px);
    width: 100%;
    flex-wrap: wrap;
}

.prize-card {
    flex: 1 1 260px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    max-width: 320px;
    margin: 0 auto;
}

.prize-image-placeholder {
    width: 100%;
    aspect-ratio: 1 / 1;
    background: rgba(255, 255, 255, 0.08);
    border: 1.5px solid rgba(254, 248, 225, 0.35);
    border-radius: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    backdrop-filter: blur(8px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s ease, border-color 0.3s ease;
}

.prize-image-placeholder:hover {
    transform: translateY(-4px);
    border-color: rgba(254, 248, 225, 0.7);
}

.prize-placeholder-inner {
    width: 100%;
    height: 100%;
    border-radius: 10px;
    background: rgba(254, 248, 225, 0.06);
    display: flex;
    justify-content: center;
    align-items: center;
}

.prize-icon {
    opacity: 0.9;
}

.prize-card-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(16px, 1.8vw, 19px);
    font-weight: 700;
    text-transform: uppercase;
    color: #FEF8E1;
    margin-top: 18px;
    margin-bottom: 0;
    line-height: 1.3;
    text-align: center;
}

/* ==========================================================================
   BLOCK 5 (ENTHUSIAST): ФОРМА ЗАЯВКИ (2 Columns: Form & Bottles)
   ========================================================================== */
.block-enthusiast-form-section {
    width: 100%;
    padding: 60px 20px 90px;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    background-image: url(../assets/img/form-1-punch-bg.png);
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.form-two-columns-layout {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: clamp(30px, 5vw, 60px);
    width: 100%;
}

.form-column-left {
    flex: 1 1 480px;
    width: 100%;
    max-width: 520px;
    text-align: left;
}

.punch-entry-form {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
}

.enthusiast-form-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.enthusiast-section-title {
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(20px, 2.2vw, 24px);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #fcf8eb;
    margin: 0 0 4px 0;
    text-align: left;
    border-bottom: 1px solid rgba(252, 248, 235, 0.25);
    padding-bottom: 8px;
    line-height: 1.25;
}

.field-item {
    display: flex;
    flex-direction: column;
    text-align: left;
    width: 100%;
}

.field-label {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 13px;
    font-weight: 600;
    color: rgba(252, 248, 235, 0.85);
    margin-bottom: 6px;
    text-align: left;
}

.field-input {
    width: 100%;
    height: 48px;
    background: transparent;
    border: 1.5px solid rgba(252, 248, 235, 0.45);
    border-radius: 8px;
    padding: 12px 16px;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 15px;
    color: #fcf8eb;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field-input::placeholder {
    color: rgba(252, 248, 235, 0.4);
    font-size: 14px;
}

.field-input:focus {
    border-color: #fcf8eb;
    box-shadow: 0 0 8px rgba(252, 248, 235, 0.3);
}

.field-textarea {
    height: auto;
    min-height: 105px;
    resize: vertical;
    line-height: 1.5;
}

.field-error-msg {
    color: #ff7676;
    font-size: 12px;
    font-family: var(--font-family, "jj2book");
    margin-top: 4px;
    text-align: left;
}

.form-checkboxes-block {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 2px;
}

.checkbox-row-item {
    display: flex;
    align-items: flex-start;
    text-align: left;
}

.custom-check-input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
}

.custom-check-label {
    cursor: pointer;
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 13px;
    line-height: 1.4;
    color: rgba(252, 248, 235, 0.9);
    position: relative;
    padding-left: 28px;
    text-align: left;
}

.custom-check-label::before {
    content: "";
    position: absolute;
    left: 0;
    top: 1px;
    width: 18px;
    height: 18px;
    border: 1.5px solid rgba(252, 248, 235, 0.55);
    border-radius: 4px;
    background-color: transparent;
    transition: background-color 0.2s, border-color 0.2s;
}

.custom-check-label::after {
    content: "";
    position: absolute;
    left: 6px;
    top: 4px;
    width: 6px;
    height: 10px;
    border: solid transparent;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    transition: border-color 0.2s;
}

.custom-check-input:checked + .custom-check-label::before {
    background-color: #fcf8eb;
    border-color: #fcf8eb;
}

.custom-check-input:checked + .custom-check-label::after {
    border-color: #06402b;
}

.form-buttons-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    margin-top: 6px;
    width: 100%;
}

.enthusiast-btn-submit {
    flex: 1 1 60%;
    min-height: 50px;
    height: 50px;
    background: #06402b;
    border: 1.5px solid rgba(252, 248, 235, 0.85);
    border-radius: 8px;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #fcf8eb;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.enthusiast-btn-submit:hover:not(:disabled) {
    background: #fcf8eb;
    color: #06402b;
    border-color: #fcf8eb;
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(252, 248, 235, 0.3);
}

.enthusiast-btn-submit:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.enthusiast-btn-clear {
    flex: 1 1 40%;
    min-height: 50px;
    height: 50px;
    background: transparent;
    border: 1.5px solid rgba(252, 248, 235, 0.6);
    border-radius: 8px;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: 15px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #fcf8eb;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
}

.enthusiast-btn-clear:hover {
    background: rgba(252, 248, 235, 0.12);
    border-color: #fcf8eb;
    transform: translateY(-2px);
}

.image-column-right {
    flex: 1 1 400px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 480px;
    align-self: center;
}

.enthusiast-image-column {
    flex: 1 1 500px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 580px;
    align-self: center;
}

.enthusiast-form-image {
    width: 100%;
    max-width: 560px;
    height: auto;
    max-height: 680px;
    object-fit: contain;
    display: block;
    margin: 0 auto;
    filter: drop-shadow(0 14px 32px rgba(0, 0, 0, 0.4));
}

.bottles-placeholder-card {
    position: relative;
    width: 100%;
    min-height: 440px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.05);
    border: 1.5px solid rgba(254, 248, 225, 0.25);
    backdrop-filter: blur(8px);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding: 30px 20px;
    box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
}

.bottles-visual-wrapper {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    position: relative;
    z-index: 2;
    width: 100%;
    height: 380px;
}

.bottle-img {
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.5));
    transition: transform 0.3s ease;
}

.bottle-center {
    width: 140px;
    z-index: 3;
    margin-bottom: 0;
}

.bottle-left {
    width: 115px;
    z-index: 2;
    transform: rotate(-10deg) translateX(25px);
    opacity: 0.9;
}

.bottle-right {
    width: 115px;
    z-index: 2;
    transform: rotate(10deg) translateX(-25px);
    opacity: 0.9;
}

.bottles-card-glow {
    position: absolute;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(254, 248, 225, 0.15) 0%, rgba(18, 76, 54, 0) 70%);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 1;
}

/* ==========================================================================
   BLOCK 10: ФУТЕР (Centered Serif Logo & 3 Lines of Text)
   ========================================================================== */
.block-footer-section {
    width: 100%;
    padding: 60px 20px 50px;
    background-color: var(--color-primary-popup);
    border-top: 1px solid rgba(254, 248, 225, 0.15);
    display: flex;
    justify-content: center;
    text-align: center;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    position: relative;
}

.footer-centered-container {
    max-width: 650px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
}

.footer-serif-logo-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
}

.footer-serif-logo-text {
    font-family: var(--font-family-serif, "Trivia", Georgia, serif);
    font-size: clamp(28px, 4vw, 36px);
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #FEF8E1;
    line-height: 1;
}

.footer-serif-subtext {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 11px;
    letter-spacing: 0.2em;
    color: rgba(254, 248, 225, 0.7);
    margin-top: 6px;
}

.footer-text-lines {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 28px;
}

.footer-line {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: clamp(14px, 1.6vw, 16px);
    color: #FEF8E1;
    opacity: 0.8;
    margin: 0;
    line-height: 1.4;
}

.footer-instagram-link {
    color: #FEF8E1;
    text-decoration: underline;
    font-weight: 600;
    transition: opacity 0.2s;
}

.footer-instagram-link:hover {
    opacity: 1;
}

.footer-reglament-wrap {
    margin-bottom: 26px;
    display: flex;
    justify-content: center;
}

.footer-reglament-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 22px;
    border-radius: 24px;
    background: rgba(254, 248, 225, 0.08);
    border: 1px solid rgba(254, 248, 225, 0.32);
    color: #FEF8E1;
    font-family: var(--font-family-bold, "jj2bold", sans-serif);
    font-size: clamp(13px, 1.5vw, 15px);
    letter-spacing: 0.04em;
    text-decoration: none;
    cursor: pointer;
    backdrop-filter: blur(4px);
    transition: all 0.25s ease;
}

.footer-reglament-link:hover {
    background: rgba(254, 248, 225, 0.18);
    border-color: #FEF8E1;
    color: #FFFFFF;
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
}

.footer-reglament-link:active {
    transform: translateY(0);
}

.footer-reglament-icon {
    width: 15px;
    height: 15px;
    stroke: currentColor;
    flex-shrink: 0;
    transition: transform 0.2s ease;
}

.footer-reglament-link:hover .footer-reglament-icon {
    transform: scale(1.1);
}

.footer-disclaimer-small {
    font-family: var(--font-family, "jj2book", sans-serif);
    font-size: 12px;
    color: rgba(254, 248, 225, 0.55);
    margin: 0;
    line-height: 1.4;
    max-width: 500px;
}

/* ==========================================================================
   POPUPS & SNACKBAR
   ========================================================================== */
.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    padding: 20px;
}

.popup-content {
    background-color: #154734;
    border: 1px solid rgba(254, 248, 225, 0.3);
    padding: 30px;
    width: 100%;
    max-width: 900px;
    border-radius: 10px;
    height: 100%;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    color: #FEF8E1;
}

.popup-header {
    padding-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid rgba(254, 248, 225, 0.2);
}

.header-text {
    font-family: var(--font-family-bold);
    font-weight: 700;
    font-size: 20px;
    text-transform: uppercase;
    color: #FEF8E1;
}

.popup-close-1 {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.popup-close-1:hover svg {
    transform: scale(1.15);
}

.popup-text-1 {
    font-family: var(--font-family-legal);
    font-weight: 400;
    font-size: 14px;
    color: #FEF8E1;
    line-height: 1.6;
    white-space: pre-line;
    padding: 20px 10px 0 0;
    overflow-y: auto;
    flex: 1;
    text-align: left;
}

.popup-text-1::-webkit-scrollbar {
    width: 6px;
}

.popup-text-1::-webkit-scrollbar-thumb {
    background-color: #FEF8E1;
    border-radius: 4px;
}

.popup-text-1::-webkit-scrollbar-track {
    background: rgba(254, 248, 225, 0.1);
    border-radius: 4px;
}

.snackbar-btn {
    color: var(--color-error, #eb5757);
    font-family: var(--font-family);
    font-size: 12px;
    font-weight: 700;
    padding: 8px 12px;
    cursor: pointer;
}

/* ==========================================================================
   RESPONSIVE MEDIA QUERIES
   ========================================================================== */
@media screen and (max-width: 900px) {
    .requirements-columns-grid,
    .form-grid-two-cols,
    .bartender-dynamic-grid {
        grid-template-columns: 1fr;
    }

    .block-hero-timer-section,
    .block-about-competition-section,
    .block-bartender-target-section,
    .block-stages-section,
    .block-judges-section,
    .block-requirements-section,
    .block-bartender-prizes-section,
    .block-faq-section,
    .block-form-notice-section,
    .block-enthusiast-about-section,
    .block-enthusiast-prizes-section,
    .block-enthusiast-form-section,
    .block-bartender-full-form-section {
        padding: 50px 20px;
    }

    .target-two-columns-layout,
    .form-two-columns-layout {
        flex-direction: column;
        align-items: center;
        gap: 28px;
    }

    .form-grid-two-cols,
    .bartender-dynamic-grid {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .target-column-left,
    .form-column-left {
        max-width: 100%;
        width: 100%;
        flex: 0;
    }

    .requirements-columns-grid {
        flex-direction: column;
        gap: 20px;
        width: 100%;
    }

    .requirement-card-column {
        max-width: 100%;
        width: 100%;
        flex: 1 1 auto;
    }

    .target-column-right,
    .image-column-right,
    .enthusiast-image-column {
        max-width: 100%;
        width: 100%;
        margin-top: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 0;
    }

    .target-bottles-image {
        max-width: 360px;
        max-height: 380px;
        width: 100%;
        height: auto;
        margin: 0 auto;
    }

    .enthusiast-form-image {
        max-width: 320px;
        max-height: 360px;
        width: 100%;
        height: auto;
        margin: 0 auto;
    }

    .timeline-content-card {
        flex-direction: column;
        align-items: flex-start;
        gap: 14px;
    }

    .stage-card-left {
        flex: 0 0 auto;
        width: 100%;
    }

    .stage-card-divider {
        display: none;
    }

    .stage-card-right {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }
}

@media screen and (max-width: 600px) {
    /* --- SECTIONS PADDINGS & GAPS --- */
    .block-hero-timer-section {
        padding: 38px 16px 32px;
    }

    .block-about-competition-section,
    .block-enthusiast-about-section {
        padding: 36px 16px;
    }

    .block-bartender-target-section {
        padding: 36px 16px;
    }

    .block-stages-section {
        padding: 36px 16px;
    }

    .block-judges-section {
        padding: 36px 16px 44px;
    }

    .block-requirements-section {
        padding: 36px 16px;
    }

    .block-bartender-prizes-section,
    .block-enthusiast-prizes-section {
        padding: 36px 16px;
    }

    .block-faq-section {
        padding: 36px 16px;
    }

    .block-form-notice-section {
        padding: 36px 16px;
    }

    .block-bartender-full-form-section,
    .block-enthusiast-form-section {
        padding: 34px 16px 48px;
    }

    .block-footer-section {
        padding: 36px 16px 28px;
    }

    /* --- TOPBAR --- */
    .role-topbar-inner {
        padding: 0 4px;
    }

    .current-role-badge {
        font-size: 11px;
        padding: 4px 8px;
    }

    .switch-role-btn {
        font-size: 11px;
        padding: 4px 10px;
    }

    /* --- HERO & COUNTDOWN TIMER --- */
    .jameson-white-logo {
        max-width: 210px;
    }

    .hero-main-title {
        font-size: clamp(28px, 8.5vw, 42px);
        margin-bottom: 12px;
    }

    .hero-subtitle {
        font-size: 15px;
    }

    .gold-divider-line {
        margin: 22px auto 18px;
    }

    .timer-heading {
        font-size: 17px;
        margin-bottom: 14px;
    }

    .countdown-timer-grid {
        gap: 6px;
        flex-wrap: nowrap;
        width: 100%;
        justify-content: center;
    }

    .timer-card {
        min-width: 64px;
        max-width: 76px;
        width: calc(25% - 6px);
        height: 72px;
        padding: 6px 2px;
        border-radius: 8px;
    }

    .timer-digit {
        font-size: 26px;
    }

    .timer-unit {
        font-size: 9px;
        margin-top: 4px;
        letter-spacing: 0.06em;
    }

    .timer-deadline-date {
        font-size: 13px;
        margin-top: 6px;
    }

    /* --- TARGET SECTION --- */
    .target-two-columns-layout {
        gap: 16px;
    }

    .target-main-heading {
        font-size: clamp(24px, 7.5vw, 34px);
        margin: 0 0 10px 0;
    }

    .target-lead-paragraph {
        font-size: 15px;
        margin: 0 0 12px 0;
        line-height: 1.4;
    }

    .bartender-requirements-list {
        gap: 6px;
    }

    .bartender-requirements-list li {
        font-size: 13px;
        padding-left: 20px;
        line-height: 1.4;
    }

    .target-bottles-image {
        max-width: 360px;
        max-height: 380px;
    }

    /* --- TIMELINE / STAGES --- */
    .timeline-content-card {
        padding: 14px 14px;
        margin-bottom: 12px;
        border-radius: 10px;
        gap: 10px;
    }

    .stage-name-title {
        font-size: 16px;
        margin-bottom: 3px;
    }

    .stage-dates-text {
        font-size: 12px;
    }

    .stage-desc-text {
        font-size: 13px;
        line-height: 1.35;
    }

    .stage-step-image {
        width: 50px;
        height: 50px;
        max-width: 50px;
        max-height: 50px;
    }

    /* --- JUDGES & PRIZES --- */
    .judges-cards-container {
        gap: 20px;
    }

    .bartender-prizes-grid,
    .prizes-cards-grid,
    .judges-top-row {
        flex-direction: column;
        align-items: center;
        gap: 20px;
    }

    .bartender-prize-card,
    .prize-card,
    .judge-card-item {
        max-width: 250px;
        width: 100%;
    }

    .judge-photo-wrapper {
        border-radius: 12px;
    }

    .judge-name-title {
        font-size: 16px;
        margin: 10px 0 2px;
    }

    .judge-role-subtitle {
        font-size: 12px;
    }

    /* --- REQUIREMENTS SECTION --- */
    .requirements-container-inner {
        width: 100%;
        max-width: 100%;
    }

    .requirements-columns-grid {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
        margin-top: 8px;
    }

    .requirement-card-column {
        width: 100% !important;
        max-width: 100% !important;
        flex: 1 1 auto !important;
        background: rgba(255, 255, 255, 0.05);
        border: 1.5px solid rgba(254, 248, 225, 0.25);
        border-radius: 14px;
        padding: 16px 14px 18px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
        box-sizing: border-box;
    }

    .req-badge-header {
        background: linear-gradient(135deg, #1a8c5b 0%, #126340 100%);
        border: 1px solid rgba(254, 248, 225, 0.3);
        border-radius: 8px;
        padding: 9px 12px;
        margin-bottom: 14px;
        text-align: center;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    .req-badge-header span {
        font-size: 14px;
        font-weight: 800;
        letter-spacing: 0.04em;
    }

    .req-checklist {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin: 0;
        padding: 0;
    }

    .req-checklist li {
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        gap: 10px;
        font-size: 13px;
        line-height: 1.4;
        text-align: left;
    }

    .req-check-icon {
        flex-shrink: 0;
        width: 20px;
        height: 20px;
        min-width: 20px;
        margin-top: 2px;
        border-radius: 50%;
        background-color: rgba(26, 140, 91, 0.5);
        border: 1.5px solid rgba(254, 248, 225, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .req-check-icon svg {
        width: 12px;
        height: 12px;
    }

    .req-checklist li span {
        font-size: 13px;
        line-height: 1.4;
        color: #FEF8E1;
        text-align: left;
        flex: 1;
    }

    .req-column-bottom-divider {
        display: none;
    }

    /* --- ENTHUSIAST STEPS --- */
    .enthusiast-steps-list {
        margin-top: 20px;
        gap: 12px;
    }

    .enthusiast-step-item {
        padding: 14px 16px;
        gap: 14px;
        border-radius: 12px;
    }

    .enthusiast-step-number {
        width: 32px;
        height: 32px;
        font-size: 15px;
    }

    .enthusiast-step-text {
        font-size: 14px;
        line-height: 1.4;
    }

    /* --- FAQ --- */
    .faq-question-btn {
        padding: 14px 12px;
    }

    .faq-question-text {
        font-size: 13px;
    }

    .faq-answer-container {
        padding: 0 12px 14px;
    }

    .faq-answer-text {
        font-size: 13px;
    }

    /* --- FORMS & ALL BUTTONS (FULL WIDTH ON MOBILE) --- */
    .form-buttons-row,
    .bartender-form-buttons-row {
        flex-direction: column !important;
        gap: 10px !important;
        width: 100% !important;
    }

    .ghost-action-btn,
    .cta-ghost-btn,
    .bartender-btn-submit,
    .bartender-btn-clear,
    .enthusiast-btn-submit,
    .enthusiast-btn-clear {
        width: 100% !important;
        max-width: 100% !important;
        flex: 1 1 100% !important;
        min-width: 100% !important;
        height: 48px !important;
        min-height: 48px !important;
        font-size: 14px !important;
        box-sizing: border-box !important;
    }

    .form-section-title,
    .enthusiast-section-title {
        font-size: 18px;
        padding-bottom: 6px;
    }

    .input-top-label,
    .field-label {
        font-size: 12px;
        margin-bottom: 4px;
    }

    .transparent-form-input,
    .field-input,
    ::v-deep .custom-v-select .v-input__control > .v-input__slot {
        height: 46px !important;
        min-height: 46px !important;
        font-size: 14px !important;
        padding: 0 14px !important;
        border-radius: 8px !important;
    }

    .transparent-textarea,
    .field-textarea {
        min-height: 85px !important;
        padding: 10px 14px !important;
    }

    .enthusiast-form-image {
        max-width: 250px;
        max-height: 280px;
    }

    .custom-check-label {
        font-size: 12px;
        line-height: 1.35;
        padding-left: 26px;
    }

    .custom-check-label::before {
        width: 17px;
        height: 17px;
        top: 0px;
    }

    .custom-check-label::after {
        left: 5px;
        top: 3px;
        width: 5px;
        height: 9px;
    }

    /* --- ROLE MODAL --- */
    .role-modal-card {
        padding: 24px 16px;
        border-radius: 14px;
        max-width: 92vw;
    }

    .role-modal-j-logo {
        width: 44px;
        margin-bottom: 12px;
    }

    .role-modal-title {
        font-size: clamp(22px, 6vw, 26px);
        margin-bottom: 6px;
    }

    .role-modal-subtitle {
        font-size: 13px;
        margin-bottom: 18px;
    }

    .role-modal-buttons {
        max-width: 100%;
        width: 100%;
        gap: 10px;
    }

    .role-choice-btn {
        width: 100% !important;
        min-width: 100% !important;
        height: 46px;
        font-size: 13px;
        padding: 10px 16px;
    }
}

@media screen and (max-width: 380px) {
    .timer-card {
        min-width: 56px;
        height: 64px;
    }

    .timer-digit {
        font-size: 22px;
    }

    .timer-unit {
        font-size: 8px;
    }

    .hero-main-title {
        font-size: 25px;
    }

    .target-main-heading,
    .about-main-title,
    .about-competition-heading {
        font-size: 22px;
    }
}
</style>

<style>
/* Vuetify Dropdown Menu Global Theme for Punch Competition */
.punch-select-menu {
    background-color: #0c3826 !important;
    border: 1px solid rgba(254, 248, 225, 0.3) !important;
    border-radius: 8px !important;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.6) !important;
}

.punch-select-menu .v-list {
    background-color: #0c3826 !important;
    padding: 6px 0 !important;
}

.punch-select-menu .v-list-item {
    color: #FEF8E1 !important;
    font-family: var(--font-family, "jj2book", sans-serif) !important;
    font-size: 15px !important;
    min-height: 44px !important;
    transition: background 0.2s ease;
}

.punch-select-menu .v-list-item:hover,
.punch-select-menu .v-list-item--active {
    background-color: rgba(254, 248, 225, 0.15) !important;
    color: #FEE79A !important;
}

.punch-select-menu .v-list-item__title {
    color: #FEF8E1 !important;
    font-size: 15px !important;
    font-weight: 500 !important;
}
</style>
